<template>
  <div class="dashboard-layout">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <div class="main-container">
        <!-- Header with Date Range Picker -->
        <div class="dashboard-header">
          <h2>{{ reportPageTitle }}</h2>
          
          <div class="date-range-picker">
            <button
              v-if="isIndependentReport"
              type="button"
              class="date-preset-button"
              @click="setAcquisitionMonthToDateRange"
            >
              Mes actual
            </button>
            <label for="start-date">Desde:</label>
            <input
              id="start-date"
              v-model="dt_dia"
              type="date"
              class="date-input"
              :max="todayDateString"
              @change="onDateRangeChange"
            />
            <label for="end-date">Hasta:</label>
            <input
              id="end-date"
              v-model="dt_dia2"
              type="date"
              class="date-input"
              :max="todayDateString"
              @change="onDateRangeChange"
            />
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading && !isIndependentReport" class="loading-indicator">
          Cargando datos...
        </div>
        <!-- Error State -->
         <div v-if="error && !isIndependentReport" class="error-message">
           <strong>Error:</strong> {{ error }}
         </div>


        <!-- Main Content Area -->
        <div class="dashboard-content">
          <div class="report-tabs" role="tablist" aria-label="Secciones de reportes">
            <button
              type="button"
              class="report-tab"
              :class="{ active: activeReportTab === 'general' }"
              role="tab"
              :aria-selected="activeReportTab === 'general'"
              @click="selectReportTab('general')"
            >
              General
            </button>
            <button
              type="button"
              class="report-tab"
              :class="{ active: activeReportTab === 'jobbing-points-repurchase' }"
              role="tab"
              :aria-selected="activeReportTab === 'jobbing-points-repurchase'"
              @click="selectReportTab('jobbing-points-repurchase')"
            >
              Recompra de puntos
            </button>
            <button
              type="button"
              class="report-tab"
              :class="{ active: activeReportTab === 'jobbing-points-retention' }"
              role="tab"
              :aria-selected="activeReportTab === 'jobbing-points-retention'"
              @click="selectReportTab('jobbing-points-retention')"
            >
              Recurrencia temprana
            </button>
            <button
              v-if="isSuperadmin"
              type="button"
              class="report-tab"
              :class="{ active: activeReportTab === 'acquisition-profitability' }"
              role="tab"
              :aria-selected="activeReportTab === 'acquisition-profitability'"
              @click="selectReportTab('acquisition-profitability')"
            >
              Rentabilidad adquisición
            </button>
            <button
              v-if="isSuperadmin"
              type="button"
              class="report-tab"
              :class="{ active: activeReportTab === 'subscriptions' }"
              role="tab"
              :aria-selected="activeReportTab === 'subscriptions'"
              @click="selectReportTab('subscriptions')"
            >
              Suscripciones
            </button>
            <button
              v-if="isSuperadmin"
              type="button"
              class="report-tab"
              :class="{ active: activeReportTab === 'professional-interactions' }"
              role="tab"
              :aria-selected="activeReportTab === 'professional-interactions'"
              @click="selectReportTab('professional-interactions')"
            >
              Interacciones de profesionales
            </button>
          </div>

          <div v-if="activeReportTab === 'general' && !isLoading && !error" class="report-tab-panel">
          <!-- Stats Cards Grid -->
          <div class="stats-grid">
            <div class="stat-card" style="background-color: #0074D9;">
              <div class="stat-card-content">
                <div class="stat-title">Clientes Únicos</div>
                <div class="stat-value">{{ uniqueClients.length }}</div>
              </div>
              <b-icon icon="person" class="stat-card-icon"></b-icon>
            </div>

            <div class="stat-card" style="background-color: #FF851B;">
              <div class="stat-card-content">
                <div class="stat-title">Total de Solicitudes</div>
                <div class="stat-value">{{ solicitudes.length }}</div>
              </div>
              <b-icon icon="chat-right-text" class="stat-card-icon"></b-icon>
            </div>

            <div class="stat-card" style="background-color: #264653;">
              <div class="stat-card-content">
                <div class="stat-title">Indice actividad pro</div>
                <div class="stat-value">
                  {{ formatActivityIndex(professionalActivity.index) }}
                  <span>pts</span>
                </div>
                <div class="stat-card-detail">
                  {{ formatCurrency(professionalActivity.active_professionals_count) }} activos / {{ formatCurrency(professionalActivity.eligible_professionals_count) }} elegibles
                </div>
                <div class="stat-card-detail" v-if="professionalActivity.average_activity_age_hours !== null">
                  Prom. ultima actividad: {{ formatActivityAge(professionalActivity.average_activity_age_hours) }}
                </div>
                <div class="stat-card-detail" v-else>
                  Sin actividad en {{ professionalActivity.active_window_hours }}h
                </div>
              </div>
              <b-icon icon="activity" class="stat-card-icon"></b-icon>
            </div>

             <div class="stat-card" style="background-color: #85144b;">
              <div class="stat-card-content">
                <div class="stat-title">Solicitudes Sin Respuesta (Clientes)</div>
                <div class="stat-value">
                  {{ unansweredClientRequestsCount }}
                  <span v-if="totalClientRequestsCount > 0">
                    ({{ unansweredPercentage }}%)
                  </span>
                  <span v-else>(N/A)</span>
                </div>
              </div>
              <b-icon icon="chat-right-text" class="stat-card-icon"></b-icon>
            </div>

            <div class="stat-card" style="background-color: #7FDBFF; color: #333;">
               <div class="stat-card-content">
                <div class="stat-title">Total Órdenes (con pago > 0)</div>
                <div class="stat-value">{{ paidOrders.length }}</div>
              </div>
              <b-icon icon="clipboard" class="stat-card-icon"></b-icon>
            </div>

            <div class="stat-card" style="background-color: #3D9970;">
              <div class="stat-card-content">
                <div class="stat-title">Ingreso Empresa</div>
                <div class="stat-value">${{ formatCurrency(totalPaymentsCompany) }}</div>
              </div>
              <b-icon icon="currency-dollar" class="stat-card-icon"></b-icon>
            </div>

             <div class="stat-card" style="background-color: #36a2eb;">
              <div class="stat-card-content">
                <div class="stat-title">Ingreso Clientes</div>
                <div class="stat-value">${{ formatCurrency(totalPaymentsClients) }}</div>
              </div>
              <b-icon icon="currency-dollar" class="stat-card-icon"></b-icon>
            </div>

            <div class="stat-card" style="background-color: #ff6384;">
               <div class="stat-card-content">
                <div class="stat-title">Total Ingreso</div>
                <div class="stat-value">${{ formatCurrency(totalPayments) }}</div>
              </div>
              <b-icon icon="currency-dollar" class="stat-card-icon"></b-icon>
            </div>
          </div>

          <div class="chart-block activity-history-block">
            <div class="chart-header-row">
              <h4>Histórico de actividad pro</h4>
              <div class="chart-controls">
                <label class="sr-only" for="activity-history-granularity">Agrupación</label>
                <select
                  id="activity-history-granularity"
                  v-model="activityHistoryGranularity"
                  class="view-select"
                  @change="fetchData"
                >
                  <option
                    v-for="option in activityHistoryGranularityOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>
            </div>
            <div class="chart-container activity-history-chart-container">
              <line-chart
                v-if="hasData(professionalActivityHistoryChartData)"
                :key="'activity-history-line-' + chartKey + '-' + activityHistoryGranularity"
                :chart-data="professionalActivityHistoryChartData"
                :options="professionalActivityHistoryChartOptions"
                :height="320"
              />
              <p v-else class="no-data-message">No hay snapshots de actividad para el período seleccionado.</p>
            </div>
          </div>

          <div class="metrics-section">
            <h3 class="metrics-section-title">Puntos Jobbing</h3>
            <div class="stats-grid jobbing-points-stats-grid">
              <div class="stat-card" style="background-color: #005F73;">
                <div class="stat-card-content">
                  <div class="stat-title">Puntos Jobbing comprados</div>
                  <div class="stat-value">{{ formatCurrency(jobbingPointsMetrics.puntos_comprados) }}</div>
                </div>
                <b-icon icon="plus-circle" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #0A9396;">
                <div class="stat-card-content">
                  <div class="stat-title">Solicitudes compradas</div>
                  <div class="stat-value">{{ formatCurrency(jobbingPointsMetrics.solicitudes_compradas_count) }}</div>
                </div>
                <b-icon icon="cart-check" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #0077B6;">
                <div class="stat-card-content">
                  <div class="stat-title">Fill rate</div>
                  <div class="stat-value">{{ formatPercentage(jobbingPointsMetrics.fill_rate) }}</div>
                  <div class="stat-card-detail">
                    {{ formatCurrency(jobbingPointsMetrics.compras_solicitudes_cohorte_count) }} compras /
                    {{ formatCurrency(jobbingPointsMetrics.cupos_compra_total) }} cupos
                  </div>
                </div>
                <b-icon icon="bar-chart-fill" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #6A4C93;">
                <div class="stat-card-content">
                  <div class="stat-title">% de solicitudes compradas</div>
                  <div class="stat-value">{{ formatPercentage(jobbingPointsMetrics.solicitudes_compradas_percentage) }}</div>
                  <div class="stat-card-detail">
                    {{ formatCurrency(jobbingPointsMetrics.solicitudes_compradas_unicas_count) }} de
                    {{ formatCurrency(jobbingPointsMetrics.solicitudes_comprables_count) }} solicitudes con precio
                  </div>
                </div>
                <b-icon icon="percent" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #CA6702;">
                <div class="stat-card-content">
                  <div class="stat-title">Puntos gastados en solicitudes</div>
                  <div class="stat-value">{{ formatCurrency(jobbingPointsMetrics.puntos_gastados_solicitudes) }}</div>
                </div>
                <b-icon icon="dash-circle" class="stat-card-icon"></b-icon>
              </div>
            </div>

            <div class="chart-block jobbing-points-timeline-block">
              <h4>Compra y uso de puntos Jobbing</h4>
              <div class="chart-container jobbing-points-timeline-container">
                <line-chart
                  v-if="hasData(jobbingPointsTimelineChartData)"
                  :key="'jobbing-points-timeline-line-' + chartKey"
                  :chart-data="jobbingPointsTimelineChartData"
                  :options="jobbingPointsTimelineChartOptions"
                  :height="320"
                />
                <p v-else class="no-data-message">No hay compras o usos de puntos Jobbing para el período seleccionado.</p>
              </div>
            </div>
          </div>

          <div class="metrics-section">
            <h3 class="metrics-section-title">Presupuestos</h3>
            <div class="stats-grid budget-stats-grid">
              <div class="stat-card" style="background-color: #2E86C1;">
                <div class="stat-card-content">
                  <div class="stat-title">Órdenes con presupuesto</div>
                  <div class="stat-value">{{ presupuestoMetrics.ordersWithBudgets }}</div>
                </div>
                <b-icon icon="clipboard-check" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #17A589;">
                <div class="stat-card-content">
                  <div class="stat-title">Total presupuestos</div>
                  <div class="stat-value">{{ presupuestoMetrics.total }}</div>
                </div>
                <b-icon icon="file-earmark-text" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #F1C40F; color: #333;">
                <div class="stat-card-content">
                  <div class="stat-title">Presupuestos enviados</div>
                  <div class="stat-value">{{ presupuestoMetrics.enviados }}</div>
                </div>
                <b-icon icon="chat-right-text" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #2ECC71;">
                <div class="stat-card-content">
                  <div class="stat-title">Presupuestos aprobados</div>
                  <div class="stat-value">{{ presupuestoMetrics.aprobados }}</div>
                </div>
                <b-icon icon="check-circle" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #E74C3C;">
                <div class="stat-card-content">
                  <div class="stat-title">Presupuestos rechazados</div>
                  <div class="stat-value">{{ presupuestoMetrics.rechazados }}</div>
                </div>
                <b-icon icon="x-circle" class="stat-card-icon"></b-icon>
              </div>
            </div>
          </div>

          <!-- Charts Section -->
          <div class="charts-section">
            <!-- Income Charts -->
            <div class="chart-block">
              <h4>Gráfico de Ingresos</h4>
              <line-chart
                v-if="hasData(incomeLineChartData)"
                :key="'income-line-' + chartKey"
                :chart-data="incomeLineChartData"
                :options="lineChartOptions"
                :height="350"
              />
               <p v-else class="no-data-message">No hay datos de ingresos para mostrar en el gráfico para el período seleccionado.</p>
              <div class="pie-charts-row">
                 <div class="chart-container pie-chart-container">
                    <h4 class="chart-title">Pagos por UTM Source</h4>
                    <pie-chart
                      v-if="hasData(paymentsByUtmSourceChartData)"
                      :key="'payments-utm-source-pie-' + chartKey"
                      :chart-data="paymentsByUtmSourceChartData"
                      :options="pieChartOptions"
                      :height="250"
                    />
                    <p v-else class="no-data-message">No hay datos de pagos por UTM Source.</p>
                  </div>
                 <div class="chart-container pie-chart-container">
                    <h4 class="chart-title">Pagos por UTM Campaign</h4>
                    <pie-chart
                      v-if="hasData(paymentsByUtmCampaignChartData)"
                      :key="'payments-utm-campaign-pie-' + chartKey"
                      :chart-data="paymentsByUtmCampaignChartData"
                      :options="pieChartOptions"
                      :height="250"
                    />
                    <p v-else class="no-data-message">No hay datos de pagos por UTM Campaign.</p>
                 </div>
              </div>
            </div>

            <!-- Requests Charts -->
            <div class="chart-block">
              <h4>Gráfico de Solicitudes</h4>
              <line-chart
                 v-if="hasData(requestsLineChartData)"
                :key="'requests-line-' + chartKey"
                :chart-data="requestsLineChartData"
                :options="requestLineChartOptions"
                :height="350"
              />
               <p v-else class="no-data-message">No hay datos de solicitudes para mostrar en el gráfico para el período seleccionado.</p>
              <div class="pie-charts-row">
                <div class="chart-container pie-chart-container">
                  <h4 class="chart-title">Solicitudes por UTM Source</h4>
                  <pie-chart
                    v-if="hasData(requestsByUtmSourceChartData)"
                    :key="'requests-utm-source-pie-' + chartKey"
                    :chart-data="requestsByUtmSourceChartData"
                    :options="pieChartOptions"
                    :height="250"
                  />
                  <p v-else class="no-data-message">No hay datos de solicitudes por UTM Source.</p>
                </div>
                <div class="chart-container pie-chart-container">
                  <h4 class="chart-title">Solicitudes por UTM Campaign</h4>
                  <pie-chart
                    v-if="hasData(requestsByUtmCampaignChartData)"
                    :key="'requests-utm-campaign-pie-' + chartKey"
                    :chart-data="requestsByUtmCampaignChartData"
                    :options="pieChartOptions"
                    :height="250"
                  />
                  <p v-else class="no-data-message">No hay datos de solicitudes por UTM Campaign.</p>
                </div>
                <div class="chart-container pie-chart-container">
                  <h4 class="chart-title">Solicitudes por Clase</h4>
                   <pie-chart
                    v-if="hasData(requestsByClassChartData)"
                    :key="'requests-class-pie-' + chartKey"
                    :chart-data="requestsByClassChartData"
                    :options="pieChartOptions"
                    :height="250"
                  />
                  <p v-else class="no-data-message">No hay datos de solicitudes por Clase.</p>
                </div>
              </div>
            </div>

            <div class="chart-block">
              <h4>Distribución de costo de solicitudes</h4>
              <div v-if="jobbingCostStats.total > 0" class="price-summary-grid">
                <div class="price-summary-item">
                  <span class="price-summary-label">Solicitudes con costo</span>
                  <strong>{{ jobbingCostStats.pricedCount }}</strong>
                </div>
                <div class="price-summary-item">
                  <span class="price-summary-label">Promedio</span>
                  <strong>{{ formatCurrency(jobbingCostStats.average) }} pts</strong>
                </div>
                <div class="price-summary-item">
                  <span class="price-summary-label">Mediana</span>
                  <strong>{{ formatCurrency(jobbingCostStats.median) }} pts</strong>
                </div>
                <div class="price-summary-item">
                  <span class="price-summary-label">Rango</span>
                  <strong>{{ formatCurrency(jobbingCostStats.min) }} - {{ formatCurrency(jobbingCostStats.max) }} pts</strong>
                </div>
              </div>
              <div class="chart-container price-distribution-chart-container">
                <bar-chart
                  v-if="hasData(jobbingCostDistributionChartData)"
                  :key="'jobbing-cost-distribution-bar-' + chartKey"
                  :chart-data="jobbingCostDistributionChartData"
                  :options="jobbingCostBarChartOptions"
                  :height="320"
                />
                <p v-else class="no-data-message">No hay costos de solicitudes para mostrar en el período seleccionado.</p>
              </div>
            </div>

            <div class="chart-block">
              <ConversionRateChart
                :key="'conversion-rate-' + chartKey + '-' + conversionViewMode"
                :chart-data="conversionRateChartData"
                :options="conversionRateChartOptions"
                :view-mode="conversionViewMode"
                :view-options="conversionViewOptions"
                @update:viewMode="onConversionViewModeChange"
              />
            </div>

             <!-- Other Charts/Data -->
             <div class="chart-block">
                <h4>Otros Gráficos</h4>
                 <div class="pie-charts-row">
                    <div class="chart-container pie-chart-container">
                      <h4 class="chart-title">Ventas por Servicio (Órdenes Pagadas)</h4>
                      <pie-chart
                        v-if="hasData(salesByServiceChartData)"
                        :key="'sales-by-service-pie-' + chartKey"
                        :chart-data="salesByServiceChartData"
                        :options="pieChartOptions"
                        :height="250"
                       />
                       <p v-else class="no-data-message">No hay datos de ventas por servicio.</p>
                 </div>
                 <div class="chart-container pie-chart-container">
                      <h4 class="chart-title">Ventas por Clase de Solicitud (Órdenes Pagadas)</h4>
                      <pie-chart
                        v-if="hasData(salesByClassChartData)"
                        :key="'sales-by-class-pie-' + chartKey"
                        :chart-data="salesByClassChartData"
                        :options="pieChartOptions"
                        :height="250"
                       />
                       <p v-else class="no-data-message">No hay datos de ventas por clase de solicitud.</p>
                 </div>
                 </div>
                 <div class="chart-container heatmap-chart-container">
                   <h4 class="chart-title">Tasa semanal de aprobación de presupuestos</h4>
                   <ProfesionalApprovalHeatmap
                     :key="'approval-heatmap-' + chartKey"
                     :weeks="heatmapData.weeks"
                     :professionals="heatmapData.professionals"
                     :values="heatmapData.values"
                   />
                 </div>
             </div>

            <!-- Client List -->
            <div class="client-list-section">
              <h4>Clientes Únicos (con órdenes pagadas en período)</h4>
              <ul v-if="uniqueClients.length > 0" class="client-list">
                <li v-for="client in sortedUniqueClients" :key="client.id">
                   {{ formatDate(client.firstOrderDate) }} - {{ client.nombre || 'Nombre no disponible' }}
                </li>
              </ul>
              <p v-else class="no-data-message">No hay clientes únicos con órdenes pagadas en este período.</p>
            </div>
          </div>
          </div>

          <div v-if="activeReportTab === 'jobbing-points-repurchase' && !isLoading && !error" class="report-tab-panel">
            <div class="repurchase-heading">
              <div>
                <h3>Recompra de puntos Jobbing</h3>
                <p>Recompra: compra pagada posterior a una compra pagada previa del profesional.</p>
              </div>
              <span class="range-pill">{{ dt_dia }} - {{ dt_dia2 }}</span>
            </div>

            <div class="stats-grid repurchase-stats-grid">
              <div class="stat-card" style="background-color: #1B4965;">
                <div class="stat-card-content">
                  <div class="stat-title">Profesionales compradores</div>
                  <div class="stat-value">{{ formatCurrency(jobbingPointsRepurchaseSummary.professionals_count) }}</div>
                </div>
                <b-icon icon="people" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #2A9D8F;">
                <div class="stat-card-content">
                  <div class="stat-title">Profesionales con recompra</div>
                  <div class="stat-value">
                    {{ formatCurrency(jobbingPointsRepurchaseSummary.professionals_with_repurchase_count) }}
                    <span>({{ formatPercentage(jobbingPointsRepurchaseSummary.repurchase_professional_rate) }})</span>
                  </div>
                </div>
                <b-icon icon="arrow-repeat" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #E76F51;">
                <div class="stat-card-content">
                  <div class="stat-title">Recompras pagadas</div>
                  <div class="stat-value">
                    {{ formatCurrency(jobbingPointsRepurchaseSummary.repurchase_count) }}
                    <span>de {{ formatCurrency(jobbingPointsRepurchaseSummary.purchase_count) }}</span>
                  </div>
                </div>
                <b-icon icon="cart-check" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #F4A261; color: #243746;">
                <div class="stat-card-content">
                  <div class="stat-title">Monto por recompra</div>
                  <div class="stat-value">${{ formatCurrency(jobbingPointsRepurchaseSummary.repurchase_amount_total) }}</div>
                </div>
                <b-icon icon="cash-stack" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #3D5A80;">
                <div class="stat-card-content">
                  <div class="stat-title">Puntos recomprados</div>
                  <div class="stat-value">{{ formatCurrency(jobbingPointsRepurchaseSummary.repurchase_points_total) }}</div>
                </div>
                <b-icon icon="plus-circle" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #6D597A;">
                <div class="stat-card-content">
                  <div class="stat-title">Solicitudes compradas</div>
                  <div class="stat-value">{{ formatCurrency(jobbingPointsRepurchaseSummary.request_purchase_count) }}</div>
                </div>
                <b-icon icon="clipboard-check" class="stat-card-icon"></b-icon>
              </div>
            </div>

            <div class="repurchase-insights-grid">
              <div class="insight-card">
                <span class="insight-label">Peso de recompras en ingresos</span>
                <strong>{{ formatPercentage(jobbingPointsRepurchaseSummary.repurchase_amount_share) }}</strong>
              </div>
              <div class="insight-card">
                <span class="insight-label">Compras promedio por profesional</span>
                <strong>{{ jobbingPointsRepurchaseSummary.average_purchases_per_professional || 0 }}</strong>
              </div>
              <div class="insight-card">
                <span class="insight-label">Primeras compras</span>
                <strong>{{ formatCurrency(jobbingPointsRepurchaseSummary.first_purchase_count) }}</strong>
              </div>
              <div class="insight-card">
                <span class="insight-label">Puntos gastados en solicitudes</span>
                <strong>{{ formatCurrency(jobbingPointsRepurchaseSummary.request_points_spent_total) }} pts</strong>
              </div>
            </div>

            <div class="repurchase-charts-grid">
              <div class="chart-block">
                <h4>Compras nuevas vs recompras</h4>
                <div class="chart-container repurchase-chart-container">
                  <bar-chart
                    v-if="hasData(jobbingPointsRepurchaseChartData)"
                    :key="'jobbing-points-repurchase-bar-' + chartKey"
                    :chart-data="jobbingPointsRepurchaseChartData"
                    :options="jobbingPointsRepurchaseBarChartOptions"
                    :height="320"
                  />
                  <p v-else class="no-data-message">No hay compras de puntos para el periodo seleccionado.</p>
                </div>
              </div>

              <div class="chart-block">
                <h4>Ingresos por tipo de compra</h4>
                <div class="chart-container repurchase-pie-container">
                  <pie-chart
                    v-if="hasData(jobbingPointsRepurchaseAmountChartData)"
                    :key="'jobbing-points-repurchase-pie-' + chartKey"
                    :chart-data="jobbingPointsRepurchaseAmountChartData"
                    :options="pieChartOptions"
                    :height="320"
                  />
                  <p v-else class="no-data-message">No hay ingresos de puntos para el periodo seleccionado.</p>
                </div>
              </div>
            </div>

            <div class="repurchase-table-section">
              <div class="repurchase-table-header">
                <h4>Profesionales con mayor recompra</h4>
                <span>{{ topJobbingPointsRepurchaseProfessionals.length }} de {{ jobbingPointsRepurchaseProfessionals.length }}</span>
              </div>

              <div v-if="topJobbingPointsRepurchaseProfessionals.length > 0" class="table-responsive">
                <table class="repurchase-table">
                  <thead>
                    <tr>
                      <th>Profesional</th>
                      <th>Compras</th>
                      <th>Recompras</th>
                      <th>Historico</th>
                      <th>Monto periodo</th>
                      <th>Monto recompra</th>
                      <th>Solicitudes</th>
                      <th>Puntos gastados</th>
                      <th>Saldo</th>
                      <th>Ultima compra</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="professional in topJobbingPointsRepurchaseProfessionals"
                      :key="professional.profesional_id"
                    >
                      <td>
                        <strong>{{ professional.nombre }}</strong>
                        <span v-if="professional.empresa_nombre" class="table-meta">{{ professional.empresa_nombre }}</span>
                        <span v-else-if="professional.email" class="table-meta">{{ professional.email }}</span>
                      </td>
                      <td>{{ formatCurrency(professional.purchase_count) }}</td>
                      <td>{{ formatCurrency(professional.repurchase_count) }}</td>
                      <td>{{ formatCurrency(professional.historical_purchase_count) }}</td>
                      <td>${{ formatCurrency(professional.amount_total) }}</td>
                      <td>${{ formatCurrency(professional.repurchase_amount) }}</td>
                      <td>{{ formatCurrency(professional.request_purchase_count) }}</td>
                      <td>{{ formatCurrency(professional.request_points_spent_total) }}</td>
                      <td>{{ formatCurrency(professional.current_balance) }}</td>
                      <td>{{ formatDateString(professional.last_period_purchase_at) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="no-data-message">No hay profesionales con compras de puntos en este periodo.</p>
            </div>
          </div>

          <div v-if="activeReportTab === 'jobbing-points-retention' && !isLoading && !error" class="report-tab-panel">
            <div class="repurchase-heading">
              <div>
                <h3>Profesionales recurrentes</h3>
                <p>
                  Mide retorno temprano usando compras pagadas de puntos y compras efectivas de
                  SolicitudCliente en semanas distintas.
                </p>
              </div>
              <span class="range-pill">Corte: {{ formatDateString(jobbingPointsRetention.as_of) }}</span>
            </div>

            <div class="retention-definition">
              <strong>Criterio de recurrencia temprana</strong>
              <p>
                Se usa todo el historial hasta la fecha “Hasta”. La fecha “Desde” no recorta esta
                métrica, porque se necesita observar la permanencia completa del profesional.
                Las compras de solicitudes restituidas no cuentan.
              </p>
              <div class="retention-criteria-list">
                <span class="retention-criterion">
                  Mín. {{ jobbingPointsRetentionCriteria.min_paid_purchases }} compra pagada de puntos
                </span>
                <span class="retention-criterion">
                  Mín. {{ jobbingPointsRetentionCriteria.min_request_purchases }} solicitudes compradas
                </span>
                <span class="retention-criterion">
                  en mín. {{ jobbingPointsRetentionCriteria.min_request_purchase_weeks }} semanas distintas
                </span>
                <span class="retention-criterion">
                  ≥ {{ jobbingPointsRetentionCriteria.min_tenure_days }} días entre activación y última solicitud
                </span>
                <span class="retention-criterion">
                  última solicitud dentro de {{ jobbingPointsRetentionCriteria.recent_request_days }} días
                </span>
              </div>
            </div>

            <div class="stats-grid repurchase-stats-grid">
              <div class="stat-card" style="background-color: #006D77;">
                <div class="stat-card-content">
                  <div class="stat-title">Recurrentes activos</div>
                  <div class="stat-value">
                    {{ formatCurrency(jobbingPointsRetentionSummary.recurring_professionals_count) }}
                  </div>
                  <div class="stat-card-detail">Cumplen todas las condiciones y siguen activos</div>
                </div>
                <b-icon icon="person-check-fill" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #2A9D8F;">
                <div class="stat-card-content">
                  <div class="stat-title">Tasa de recurrencia temprana</div>
                  <div class="stat-value">
                    {{ formatPercentage(jobbingPointsRetentionSummary.early_retention_rate) }}
                  </div>
                  <div class="stat-card-detail">
                    Sobre profesionales activados con al menos
                    {{ jobbingPointsRetentionCriteria.min_tenure_days }} días para madurar
                  </div>
                </div>
                <b-icon icon="graph-up" class="stat-card-icon"></b-icon>
              </div>

              <div class="stat-card" style="background-color: #3D5A80;">
                <div class="stat-card-content">
                  <div class="stat-title">Cohorte evaluable</div>
                  <div class="stat-value">
                    {{ formatCurrency(jobbingPointsRetentionSummary.eligible_professionals_count) }}
                  </div>
                  <div class="stat-card-detail">Compraron puntos, compraron una solicitud y maduraron</div>
                </div>
                <b-icon icon="people-fill" class="stat-card-icon"></b-icon>
              </div>

              <button
                type="button"
                class="stat-card stat-card-button"
                style="background-color: #9C6644;"
                aria-haspopup="dialog"
                @click="openLapsedRetentionProfessionals"
              >
                <span class="stat-card-content">
                  <span class="stat-title">Recurrentes dormidos</span>
                  <span class="stat-value">
                    {{ formatCurrency(jobbingPointsRetentionSummary.lapsed_recurring_professionals_count) }}
                  </span>
                  <span class="stat-card-detail">
                    Cumplieron recurrencia histórica, pero llevan más de
                    {{ jobbingPointsRetentionCriteria.recent_request_days }} días sin comprar solicitudes
                  </span>
                  <span class="stat-card-action">Ver profesionales →</span>
                </span>
                <b-icon icon="clock-history" class="stat-card-icon" aria-hidden="true"></b-icon>
              </button>
            </div>

            <b-modal
              id="lapsed-retention-modal"
              v-model="showLapsedRetentionProfessionals"
              title="Recurrentes dormidos"
              size="xl"
              centered
              scrollable
              ok-only
              ok-title="Cerrar"
              header-close-label="Cerrar"
            >
              <p class="lapsed-retention-description">
                Profesionales con recurrencia histórica y más de
                {{ jobbingPointsRetentionCriteria.recent_request_days }} días sin comprar solicitudes
                al {{ formatDateString(jobbingPointsRetention.as_of) }}.
                Compras y montos acumulados hasta ese corte; se excluyen las solicitudes restituidas.
              </p>
              <div class="lapsed-retention-toolbar">
                <label for="lapsed-retention-search" class="sr-only">Buscar profesionales dormidos</label>
                <input
                  id="lapsed-retention-search"
                  v-model="lapsedRetentionSearch"
                  type="search"
                  class="form-control"
                  placeholder="Buscar por nombre, empresa, correo, teléfono o ID"
                >
                <span aria-live="polite">
                  {{ filteredLapsedRetentionProfessionals.length }} de
                  {{ jobbingPointsLapsedRetentionProfessionals.length }} profesionales
                </span>
              </div>
              <p class="table-meta">Ordenados de mayor a menor tiempo sin comprar solicitudes.</p>
              <div
                v-if="filteredLapsedRetentionProfessionals.length"
                class="table-responsive lapsed-retention-table-container"
                role="region"
                aria-label="Listado de recurrentes dormidos"
                tabindex="0"
              >
                <table class="repurchase-table lapsed-retention-table">
                  <thead>
                    <tr>
                      <th scope="col">Profesional</th>
                      <th scope="col">Contacto</th>
                      <th scope="col">Días sin comprar solicitud</th>
                      <th scope="col">Última solicitud</th>
                      <th scope="col">Solicitudes compradas</th>
                      <th scope="col">Compras de puntos</th>
                      <th scope="col">Monto pagado en puntos</th>
                      <th scope="col">Última compra de puntos</th>
                      <th scope="col">Semanas comprando solicitudes</th>
                      <th scope="col">Permanencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="professional in filteredLapsedRetentionProfessionals"
                      :key="professional.profesional_id"
                    >
                      <td>
                        <strong>{{ professional.nombre }}</strong>
                        <span class="table-meta">ID {{ professional.profesional_id }}</span>
                        <span v-if="professional.empresa_nombre" class="table-meta">{{ professional.empresa_nombre }}</span>
                      </td>
                      <td class="lapsed-retention-contact">
                        <span>{{ professional.email || 'Sin correo' }}</span>
                        <span class="table-meta">{{ professional.telefono || 'Sin teléfono' }}</span>
                      </td>
                      <td><strong>{{ formatCurrency(professional.days_since_last_request) }} días</strong></td>
                      <td>{{ formatDateString(professional.last_request_purchase_at) }}</td>
                      <td>{{ formatCurrency(professional.request_purchase_count) }}</td>
                      <td>{{ formatCurrency(professional.paid_purchase_count) }}</td>
                      <td>${{ formatCurrency(professional.amount_paid_total) }}</td>
                      <td>{{ formatDateString(professional.last_paid_purchase_at) }}</td>
                      <td>{{ formatCurrency(professional.request_purchase_weeks) }}</td>
                      <td>
                        {{ formatCurrency(professional.tenure_days) }} días
                        <span class="table-meta">Activación: {{ formatDateString(professional.activation_at) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="no-data-message">
                {{ jobbingPointsLapsedRetentionProfessionals.length
                  ? 'No hay profesionales que coincidan con la búsqueda.'
                  : 'No hay recurrentes dormidos para esta fecha de corte.' }}
              </p>
            </b-modal>

            <div class="repurchase-insights-grid">
              <div class="insight-card">
                <span class="insight-label">Profesionales activados</span>
                <strong>{{ formatCurrency(jobbingPointsRetentionSummary.activated_professionals_count) }}</strong>
              </div>
              <div class="insight-card">
                <span class="insight-label">Con uso reciente</span>
                <strong>{{ formatCurrency(jobbingPointsRetentionSummary.recently_active_professionals_count) }}</strong>
              </div>
              <div class="insight-card">
                <span class="insight-label">Recurrencia histórica</span>
                <strong>{{ formatCurrency(jobbingPointsRetentionSummary.historically_recurring_professionals_count) }}</strong>
              </div>
              <div class="insight-card">
                <span class="insight-label">Activados aún en observación</span>
                <strong>{{ formatCurrency(jobbingPointsRetentionSummary.excluded_immature_professionals_count) }}</strong>
              </div>
            </div>

            <div class="chart-block retention-history-block">
              <div class="chart-header-row">
                <div>
                  <h4>Evolución semanal de recurrencia</h4>
                  <span class="chart-header-detail">
                    Hasta 52 semanas de historia reconstruidas al cierre de cada semana
                  </span>
                </div>
                <span v-if="jobbingPointsRetentionHistory.series.length" class="range-pill">
                  {{ jobbingPointsRetentionHistory.series.length }} cortes
                </span>
              </div>
              <div class="chart-container retention-history-chart-container">
                <p v-if="isRetentionHistoryLoading" class="no-data-message">
                  Calculando evolución semanal...
                </p>
                <p v-else-if="retentionHistoryError" class="history-error-message">
                  {{ retentionHistoryError }}
                </p>
                <line-chart
                  v-else-if="hasData(jobbingPointsRetentionHistoryChartData)"
                  :key="'jobbing-points-retention-history-' + chartKey"
                  :chart-data="jobbingPointsRetentionHistoryChartData"
                  :options="jobbingPointsRetentionHistoryChartOptions"
                  :height="380"
                />
                <p v-else class="no-data-message">
                  Aún no hay suficientes eventos para construir la evolución.
                </p>
              </div>
            </div>

            <div class="repurchase-table-section">
              <div class="repurchase-table-header">
                <h4>Profesionales que cumplen el criterio de recurrencia</h4>
                <span>{{ jobbingPointsRetentionProfessionals.length }} profesionales</span>
              </div>

              <div v-if="jobbingPointsRetentionProfessionals.length > 0" class="table-responsive">
                <table class="repurchase-table retention-table">
                  <thead>
                    <tr>
                      <th>Profesional</th>
                      <th>Compras de puntos</th>
                      <th>Solicitudes compradas</th>
                      <th>Semanas comprando solicitudes</th>
                      <th>Permanencia</th>
                      <th>Última solicitud</th>
                      <th>Días sin comprar solicitud</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="professional in jobbingPointsRetentionProfessionals"
                      :key="professional.profesional_id"
                    >
                      <td>
                        <strong>{{ professional.nombre }}</strong>
                        <span v-if="professional.empresa_nombre" class="table-meta">{{ professional.empresa_nombre }}</span>
                        <span v-else-if="professional.email" class="table-meta">{{ professional.email }}</span>
                      </td>
                      <td>{{ formatCurrency(professional.paid_purchase_count) }}</td>
                      <td>{{ formatCurrency(professional.request_purchase_count) }}</td>
                      <td>{{ formatCurrency(professional.request_purchase_weeks) }}</td>
                      <td>{{ formatCurrency(professional.tenure_days) }} días</td>
                      <td>{{ formatDateString(professional.last_request_purchase_at) }}</td>
                      <td>{{ formatCurrency(professional.days_since_last_request) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="no-data-message">
                Ningún profesional cumple todavía todas las condiciones de recurrencia temprana.
              </p>
            </div>
          </div>

          <div
            v-if="activeReportTab === 'acquisition-profitability' && isSuperadmin"
            class="report-tab-panel"
          >
            <AcquisitionProfitabilityReport
              :start-date="dt_dia"
              :end-date="dt_dia2"
            />
          </div>
          <div v-if="activeReportTab === 'subscriptions' && isSuperadmin" class="report-tab-panel">
            <SubscriptionReport :start-date="dt_dia" :end-date="dt_dia2" />
          </div>
          <div v-if="activeReportTab === 'professional-interactions' && isSuperadmin" class="report-tab-panel">
            <ProfessionalInteractionsReport :start-date="dt_dia" :end-date="dt_dia2" />
          </div>
        </div> <!-- end dashboard-content -->
      </div> <!-- end main-container -->
    </div> <!-- end column-right -->
  </div> <!-- end dashboard-layout -->
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import SideNav from '@/components/SideNav.vue';
import TopNav from '@/components/TopNav.vue';
import ProfesionalApprovalHeatmap from '@/components/ProfesionalApprovalHeatmap.vue';
import LineChart from '@/components/charts/LineChart.js';
import PieChart from '@/components/charts/PieChart.js';
import BarChart from '@/components/charts/BarChart.js';
import ConversionRateChart from '@/components/ConversionRateChart.vue';
import AcquisitionProfitabilityReport from '@/components/reports/AcquisitionProfitabilityReport.vue';
import SubscriptionReport from '@/components/reports/SubscriptionReport.vue';
import ProfessionalInteractionsReport from '@/components/reports/ProfessionalInteractionsReport.vue';
/* import PieChart from "./PieChart.vue"; */
// Ensure Chart.js and adapters are installed and potentially registered (in main.js or here if needed)
// import { Chart, registerables } from 'chart.js';
// import 'chartjs-adapter-date-fns'; // Or your preferred adapter
// Chart.register(...registerables); // Example global registration

// --- Helper Functions ---

function groupBy(array, key) {
  console.log(`[groupBy] Grouping by key: ${key}`);
  if (!Array.isArray(array)) {
    console.warn('[groupBy] Input is not an array.');
    return {};
  }
  const result = array.reduce((result, currentValue) => {
    // Added check for object type
    if (currentValue !== null && typeof currentValue === 'object') {
      const groupKey = currentValue[key];
      // Ensure groupKey is defined before using it
      if (groupKey !== undefined && groupKey !== null) {
          (result[groupKey] = result[groupKey] || []).push(currentValue);
      } else {
           // Optionally group items with missing keys
           // (result['__missing__'] = result['__missing__'] || []).push(currentValue);
      }
    }
    return result;
  }, {});
  // console.log('[groupBy] Result:', result); // Logs the grouped object
  return result;
}


function getDateRangeArray(startDateStr, endDateStr) {
    const dates = [];
    if (!startDateStr || !endDateStr) return dates; // Return empty if dates missing

    // Use UTC dates to avoid timezone causing off-by-one day issues
    // This assumes the date strings are YYYY-MM-DD
    let currentDate = new Date(Date.UTC(
        parseInt(startDateStr.substring(0, 4)),
        parseInt(startDateStr.substring(5, 7)) - 1, // Month is 0-indexed
        parseInt(startDateStr.substring(8, 10))
    ));
    const stopDate = new Date(Date.UTC(
        parseInt(endDateStr.substring(0, 4)),
        parseInt(endDateStr.substring(5, 7)) - 1,
        parseInt(endDateStr.substring(8, 10))
    ));


    if (isNaN(currentDate.getTime()) || isNaN(stopDate.getTime()) || currentDate > stopDate) {
        console.error("Invalid date range for getDateRangeArray:", startDateStr, endDateStr);
        return [];
    }

    while (currentDate <= stopDate) {
        // Format back to YYYY-MM-DD string
        dates.push(currentDate.toISOString().split('T')[0]);
        // Increment day in UTC
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }
    return dates;
}


function aggregateChartData(items, groupByKey, valueKey = null, defaultValue = "Sin identificar") {
    console.log(`[aggregateChartData] Aggregating by: ${groupByKey}, Value key: ${valueKey}`);
    if (!Array.isArray(items)) {
        console.warn('[aggregateChartData] Input is not an array.');
        return new Map();
    }

    const aggregation = new Map();
    items.forEach((item, index) => {
        if (typeof item !== 'object' || item === null) {
            // console.warn(`[aggregateChartData] Skipping invalid item at index ${index}:`, item);
            return;
        }

        let key;
        // Robust nested key access
        const getNestedValue = (obj, path) => {
            if (typeof path !== 'string') return undefined;
            const keys = path.split('.');
            let current = obj;
            for (const k of keys) {
                if (current === null || typeof current !== 'object' || current[k] === undefined || current[k] === null) {
                    return null; // Return null if path is broken or value is null/undefined
                }
                current = current[k];
            }
            return current;
        };

        key = getNestedValue(item, groupByKey);

        const finalKey = (key !== null && key !== undefined && key !== '') ? key : defaultValue;
        const currentValue = aggregation.get(finalKey) || 0;

        if (valueKey) {
             let itemValue = getNestedValue(item, valueKey);
             // Ensure itemValue is a number, default to 0 if not
             itemValue = (typeof itemValue === 'number' && !isNaN(itemValue)) ? itemValue : 0;
             aggregation.set(finalKey, currentValue + itemValue);
        } else {
            aggregation.set(finalKey, currentValue + 1); // Count occurrences
        }
    });
     // console.log('[aggregateChartData] Result Map:', aggregation);
    return aggregation;
}

function formatPieChartData(aggregationMap, label) {
     // console.log(`[formatPieChartData] Formatting for label: ${label}`);
    const emptyStructure = { labels: [], datasets: [{ label: label, data: [], backgroundColor: [] }] };
    if (!(aggregationMap instanceof Map) || aggregationMap.size === 0) {
         // console.log('[formatPieChartData] Aggregation map is empty or invalid.');
         return emptyStructure;
     }

    const labels = Array.from(aggregationMap.keys());
    const data = Array.from(aggregationMap.values());

    // Filter out entries where data is 0 (optional, but cleans up pies)
    const filteredLabels = [];
    const filteredData = [];
    labels.forEach((lbl, index) => {
        if (data[index] > 0) {
            filteredLabels.push(lbl);
            filteredData.push(data[index]);
        }
    });

     if (filteredLabels.length === 0) {
         // console.log('[formatPieChartData] All data values are zero.');
         return emptyStructure;
     }


    const backgroundColors = [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
        '#E7E9ED', '#83B3E3', '#F4A460', '#98FB98', '#DDA0DD', '#CD5C5C'
    ];
     const chartColors = filteredLabels.map((_, index) => backgroundColors[index % backgroundColors.length]);

    const chartData = {
        labels: filteredLabels,
        datasets: [{
            label: label,
            backgroundColor: chartColors,
            data: filteredData,
            hoverBorderWidth: 4,
            hoverOffset: 8
        }]
    };
     // console.log('[formatPieChartData] Final chart data:', chartData);
     return chartData;
}

function createEmptyHeatmapData() {
  return { weeks: [], professionals: [], values: [] };
}

function createEmptyJobbingPointsMetrics() {
  return {
    compras_puntos_count: 0,
    puntos_comprados: 0,
    monto_puntos_comprados: 0,
    solicitudes_compradas_count: 0,
    puntos_gastados_solicitudes: 0,
    solicitudes_comprables_count: 0,
    solicitudes_compradas_unicas_count: 0,
    solicitudes_compradas_percentage: 0,
    max_compras_por_solicitud: 0,
    cupos_compra_total: 0,
    compras_solicitudes_cohorte_count: 0,
    fill_rate: 0,
  };
}

function createEmptyJobbingPointsTimeline() {
  return {
    granularity: 'day',
    summary: {
      purchase_count: 0,
      request_purchase_count: 0,
      points_purchased: 0,
      amount_purchased: 0,
      points_spent: 0,
    },
    series: [],
  };
}

function createEmptyProfessionalActivityIndex() {
  return {
    index: 0,
    freshness_score: 0,
    normalized_index: 0,
    active_professionals_count: 0,
    eligible_professionals_count: 0,
    active_window_hours: 24,
    average_activity_age_hours: null,
    freshest_activity_at: null,
    cutoff_at: null,
    calculated_at: null,
  };
}

function createEmptyProfessionalActivityHistory() {
  return {
    scope_key: '',
    scope_type: 'global',
    empresa_id: null,
    granularity: 'day',
    window_hours: 24,
    formula_version: 'v1',
    series: [],
  };
}

function createEmptyJobbingPointsRepurchase() {
  return {
    summary: {
      professionals_count: 0,
      professionals_with_repurchase_count: 0,
      purchase_count: 0,
      first_purchase_count: 0,
      repurchase_count: 0,
      points_total: 0,
      amount_total: 0,
      repurchase_points_total: 0,
      repurchase_amount_total: 0,
      request_purchase_count: 0,
      request_points_spent_total: 0,
      repurchase_professional_rate: 0,
      repurchase_purchase_rate: 0,
      repurchase_amount_share: 0,
      average_purchases_per_professional: 0,
    },
    professionals: [],
    monthly_series: [],
  };
}

function createEmptyJobbingPointsRetention() {
  return {
    as_of: null,
    criteria: {
      min_paid_purchases: 1,
      min_request_purchases: 4,
      min_request_purchase_weeks: 2,
      min_tenure_days: 7,
      recent_request_days: 21,
    },
    summary: {
      activated_professionals_count: 0,
      eligible_professionals_count: 0,
      excluded_immature_professionals_count: 0,
      recently_active_professionals_count: 0,
      historically_recurring_professionals_count: 0,
      recurring_professionals_count: 0,
      lapsed_recurring_professionals_count: 0,
      early_retention_rate: 0,
    },
    professionals: [],
    lapsed_professionals: [],
  };
}

function createEmptyJobbingPointsRetentionHistory() {
  return {
    as_of: null,
    granularity: 'week',
    window_weeks: 52,
    criteria: {},
    series: [],
  };
}

function subtractDays(dateStr, days) {
  if (!dateStr) return '';
  const baseDate = new Date(dateStr);
  if (Number.isNaN(baseDate.getTime())) {
    return '';
  }
  baseDate.setDate(baseDate.getDate() - days);
  return baseDate.toISOString().split('T')[0];
}

function getMonthSequenceBetween(startDateStr, endDateStr) {
  if (!endDateStr) {
    return [];
  }

  const endDate = new Date(`${endDateStr}T00:00:00Z`);
  if (Number.isNaN(endDate.getTime())) {
    console.error('Invalid endDateStr for getMonthSequenceBetween:', endDateStr);
    return [];
  }

  let startDate = null;
  if (startDateStr) {
    const parsedStart = new Date(`${startDateStr}T00:00:00Z`);
    if (!Number.isNaN(parsedStart.getTime())) {
      startDate = parsedStart;
    }
  }

  if (!startDate) {
    startDate = new Date(endDate);
  }

  if (startDate > endDate) {
    console.warn('Start date is after end date in getMonthSequenceBetween:', startDateStr, endDateStr);
    return [];
  }

  const firstMonth = new Date(Date.UTC(
    startDate.getUTCFullYear(),
    startDate.getUTCMonth(),
    1,
  ));

  const lastMonth = new Date(Date.UTC(
    endDate.getUTCFullYear(),
    endDate.getUTCMonth(),
    1,
  ));

  const months = [];
  const cursor = new Date(firstMonth);
  while (cursor <= lastMonth) {
    const isoKey = cursor.toISOString().split('T')[0];
    months.push({
      key: isoKey,
      monthKey: `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, '0')}`,
      label: cursor.toLocaleDateString('es-ES', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }),
      axisLabel: cursor.toLocaleDateString('es-ES', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }),
    });

    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  return months;
}

function formatMonthLabel(dateStr) {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatDayLabel(dateStr) {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function solicitudHasPaidOrder(solicitud) {
  if (!solicitud || !Array.isArray(solicitud.ordenes)) {
    return false;
  }

  return solicitud.ordenes.some((orden) =>
    Array.isArray(orden.pagos) && orden.pagos.some((pago) => {
      if (!pago) return false;
      const amount = typeof pago.monto === 'number' ? pago.monto : parseFloat(pago.monto);
      return pago.estado === 1 && Number.isFinite(amount) && amount > 0;
    })
  );
}

function solicitudHasClientResponse(solicitud) {
  if (!solicitud) {
    return false;
  }

  if (typeof solicitud.mensajes_cliente_count === 'number') {
    return solicitud.mensajes_cliente_count > 0;
  }

  const hasClientMessage = (messages) =>
    Array.isArray(messages) &&
    messages.some((message) => {
      if (!message || typeof message !== 'object') {
        return false;
      }
      const senderType = message.sender_type || message.senderType || message.sender;
      if (typeof senderType === 'string') {
        return senderType.toLowerCase() === 'cliente';
      }
      return false;
    });

  if (hasClientMessage(solicitud.mensajes_usuario)) {
    return true;
  }

  if (hasClientMessage(solicitud.mensajes)) {
    return true;
  }

  return false;
}

function createPercentageTicks(values) {
  const finiteValues = Array.isArray(values)
    ? values.filter((value) => Number.isFinite(value) && value >= 0)
    : [];

  const ticks = {
    beginAtZero: true,
    min: 0,
    callback: (value) => `${value}%`,
  };

  if (finiteValues.length === 0) {
    return {
      ...ticks,
      suggestedMax: 100,
      max: 100,
      stepSize: 10,
    };
  }

  const rawMax = finiteValues.reduce((max, value) => (value > max ? value : max), 0);
  const expandedMax = Math.max(rawMax + 1, rawMax * 1.1);
  const cappedMax = Math.min(100, expandedMax);
  const normalizedMax = cappedMax < 5 ? 5 : cappedMax;
  let roundedMax = Math.ceil(normalizedMax / 5) * 5;
  let stepSize;

  if (roundedMax <= 10) {
    stepSize = 1;
  } else if (roundedMax <= 40) {
    stepSize = 5;
  } else {
    stepSize = 10;
    roundedMax = Math.ceil(roundedMax / 10) * 10;
  }

  return {
    ...ticks,
    suggestedMax: roundedMax,
    max: roundedMax,
    stepSize,
  };
}

function normalizeJobbingCost(value) {
  const numericValue = typeof value === 'number'
    ? value
    : parseInt(value, 10);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  return numericValue;
}

function calculateMedian(sortedValues) {
  if (!Array.isArray(sortedValues) || sortedValues.length === 0) {
    return 0;
  }

  const middle = Math.floor(sortedValues.length / 2);
  if (sortedValues.length % 2 === 0) {
    return Math.round((sortedValues[middle - 1] + sortedValues[middle]) / 2);
  }

  return sortedValues[middle];
}

function isJobbingPointsPayment(pago) {
  if (!pago || typeof pago !== 'object') return false;
  return Boolean(
    pago.es_compra_puntos_jobbing ||
    pago.jobbing_points_purchase_id ||
    pago.creado_por === 'jobbing_points'
  );
}

// --- Vue Component ---
export default {
  name: 'DashboardGeneral',
  components: {
    SideNav,
    TopNav,
    LineChart,
    PieChart,
    BarChart,
    ProfesionalApprovalHeatmap,
    ConversionRateChart,
    AcquisitionProfitabilityReport,
    SubscriptionReport,
    ProfessionalInteractionsReport,
  },
  data() {
    return {
      isLoading: false, // Renamed from 'loaded' for clarity
      error: null,
      dt_dia: '',
      dt_dia2: '',
      rawSolicitudes: [], // Store raw API data
      rawPagos: [],       // Store raw API data
      heatmapData: createEmptyHeatmapData(),
      jobbingPointsMetrics: createEmptyJobbingPointsMetrics(),
      jobbingPointsTimeline: createEmptyJobbingPointsTimeline(),
      professionalActivityMetrics: createEmptyProfessionalActivityIndex(),
      professionalActivityHistory: createEmptyProfessionalActivityHistory(),
      jobbingPointsRepurchase: createEmptyJobbingPointsRepurchase(),
      jobbingPointsRetention: createEmptyJobbingPointsRetention(),
      showLapsedRetentionProfessionals: false,
      lapsedRetentionSearch: '',
      jobbingPointsRetentionHistory: createEmptyJobbingPointsRetentionHistory(),
      isRetentionHistoryLoading: false,
      retentionHistoryError: null,
      retentionHistoryLoadedFor: null,
      activeReportTab: 'general',
      acquisitionRangeInitialized: false,
      subscriptionRangeInitialized: false,
      chartKey: 0,
      activityHistoryGranularity: 'day',
      activityHistoryGranularityOptions: [
        { value: 'hour', label: 'Por hora' },
        { value: 'day', label: 'Por día' },
        { value: 'week', label: 'Por semana' },
      ],
      conversionViewMode: 'daily',
      conversionViewOptions: [
        { value: 'daily', label: 'Diario (promedio móvil 30 días - conversión)' },
        { value: 'dailyClientResponse', label: 'Diario (promedio móvil 30 días - respuesta clientes)' },
        { value: 'dailyRate', label: 'Diario (tasa por día)' },
        { value: 'monthly', label: 'Mensual (agrupado por mes)' },
      ],

      // Chart.js Options (remain the same as previous correct version)
      pieChartOptions: { /* ... options from previous version ... */
        responsive: true, maintainAspectRatio: false, legend: { display: true, position: 'right', labels: { boxWidth: 12 } }, title: { display: false },
        tooltips: { callbacks: { label: function(tooltipItem, data) { let label = data.labels[tooltipItem.index] || ''; let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]; if (label) { label += ': '; } if (Math.abs(value) > 1000 || (value % 1 !== 0 && value !== 0)) { label += '$' + value.toLocaleString('de-DE'); } else { label += value.toLocaleString('de-DE'); } return label; } } },
        // plugins: { datalabels: { /* ... config ... */ }} // Enable if installed
      },
      baseLineChartOptions: { /* ... options from previous version ... */
         responsive: true, maintainAspectRatio: false, title: { display: false }, scales: { xAxes: [{ type: "time", time: { unit: 'day', displayFormats: { day: 'MMM DD' }, tooltipFormat: 'll' }, scaleLabel: { display: true, labelString: 'Día' }, ticks: { source: 'auto', maxRotation: 45, autoSkip: true, } }], yAxes: [{ ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'Valor' } }] }, tooltips: { mode: 'index', intersect: false, callbacks: { label: function(tooltipItem, data) { return `${data.datasets[tooltipItem.datasetIndex].label}: ${tooltipItem.yLabel}`; } } }, hover: { mode: 'nearest', intersect: true }, legend: { position: 'bottom' }
      }
    };
  },

  computed: {
    ...mapGetters(['servicios', 'admin_datos']),

    isSuperadmin() {
      return Boolean(
        this.admin_datos
        && (this.admin_datos.superadmin === 1 || this.admin_datos.superadmin === true)
      );
    },

    todayDateString() {
      return this.localDateString(new Date());
    },

    isIndependentReport() {
      return ['acquisition-profitability', 'subscriptions', 'professional-interactions'].includes(this.activeReportTab);
    },

    reportPageTitle() {
      if (this.activeReportTab === 'professional-interactions') return 'Interacciones de profesionales';
      if (this.activeReportTab === 'subscriptions') return 'Estadísticas de suscripciones';
      return this.activeReportTab === 'acquisition-profitability'
        ? 'Rentabilidad de adquisición'
        : 'Dashboard General';
    },

    // --- Processed Data for Computations ---
    // Add the 'fecha_iso_str' for reliable grouping
    solicitudes() {
        // console.log('[Computed: solicitudes] Processing rawSolicitudes:', this.rawSolicitudes); // Keep if needed
        if (!Array.isArray(this.rawSolicitudes)) return [];

        const startDate = this.dt_dia || null;
        const endDate = this.dt_dia2 || null;

        const processed = this.rawSolicitudes
            .filter(s => s && s.fecha_creado)
            .map(s => ({
                ...s,
                fecha_iso_str: s.fecha_creado.split('T')[0],
                empresa: !!s.empresa
             }));

        return processed.filter(solicitud => {
            if (!solicitud.fecha_iso_str) return false;
            if (startDate && solicitud.fecha_iso_str < startDate) return false;
            if (endDate && solicitud.fecha_iso_str > endDate) return false;
            return true;
        });
    },
    // Add 'fecha_iso_str' and ensure monto is number
    pagos() {
         console.log('[Computed: pagos] Processing rawPagos (expecting nested solicitud_cliente):', this.rawPagos);
        if (!Array.isArray(this.rawPagos)) return [];

        const startDate = this.dt_dia || null;
        const endDate = this.dt_dia2 || null;

        const processed = this.rawPagos
            .filter(p => p  && p.estado === 1) // Filter confirmed payments with dates
            .map((p, index) => {
                // Ensure monto is a number
                const monto = (typeof p.monto === 'number' && !isNaN(p.monto)) ? p.monto : 0;

                // **Crucially, preserve the solicitud_cliente object received from API**
                const solicitudClienteData = (p.solicitud_cliente && typeof p.solicitud_cliente === 'object')
                                            ? p.solicitud_cliente
                                            : null; // Keep as null if missing/invalid


                return {
                    // Include essential fields from the original pago object 'p'
                    id: p.id,
                    orden_id: p.orden_id,
                    monto: monto,
                    estado: p.estado,
                    metodo_pago: p.metodo_pago,
                    creado_por: p.creado_por,
                    recibido_por: p.recibido_por,
                    es_compra_puntos_jobbing: isJobbingPointsPayment(p),
                    jobbing_points_purchase_id: p.jobbing_points_purchase_id || null,
                    fecha_creado: p.fecha_creado,
                    empresa: !!p.empresa, // Ensure boolean
                    // Add the derived/formatted fields
                    fecha_iso_str: p.fecha_creado.split('T')[0],
                    // Assign the (potentially null) nested object
                    solicitud_cliente: solicitudClienteData,
                    // Include any other fields from 'p' if needed elsewhere
                };
            });

        return processed.filter(pago => {
            if (!pago.fecha_iso_str) return false;
            if (startDate && pago.fecha_iso_str < startDate) return false;
            if (endDate && pago.fecha_iso_str > endDate) return false;
            return true;
        });
    },
    allOrders() {
        // This logic should still be okay, assuming 'ordenes' are nested under 'solicitudes' correctly
        console.log('[Computed: allOrders] Processing solicitudes:', this.solicitudes);
        if (!Array.isArray(this.solicitudes)) return [];
        return this.solicitudes.flatMap(solicitud =>
            (solicitud.ordenes || []).map(orden => {
                 const pagosOrder = (orden.pagos || [])
                    .filter(pago => pago.estado === 1)
                    .reduce((sum, pago) => sum + ((typeof pago.monto === 'number') ? pago.monto : 0), 0);
                 return { /* ... as before ... */
                    ...orden, total_pagos_realizados: pagosOrder,
                    solicitud_cliente: { utm_source: solicitud.utm_source, utm_campaign: solicitud.utm_campaign, clase: solicitud.clase, },
                    fecha_obj: solicitud.fecha_creado ? new Date(solicitud.fecha_creado) : null,
                    servicios: orden.servicios || [], cliente: orden.cliente || null,
                    fecha_fallback: (orden.fecha && orden.fecha.a) ? new Date(orden.fecha.a, orden.fecha.m - 1, orden.fecha.d) : null
                 };
            })
        );
    },
    allOrders() {
        console.log('[Computed: allOrders] Processing solicitudes:', this.solicitudes); // Use processed solicitudes here
        if (!Array.isArray(this.solicitudes)) return [];
        return this.solicitudes.flatMap(solicitud =>
            (solicitud.ordenes || []).map(orden => {
                 const pagosOrder = (orden.pagos || [])
                    .filter(pago => pago.estado === 1) // Only confirmed payments for this order
                    .reduce((sum, pago) => sum + ((typeof pago.monto === 'number') ? pago.monto : 0), 0);

                 return {
                    ...orden,
                    total_pagos_realizados: pagosOrder,
                    solicitud_cliente: {
                        utm_source: solicitud.utm_source,
                        utm_campaign: solicitud.utm_campaign,
                        clase: solicitud.clase,
                    },
                    fecha_obj: solicitud.fecha_creado ? new Date(solicitud.fecha_creado) : null,
                    servicios: orden.servicios || [],
                    cliente: orden.cliente || null,
                    fecha_fallback: (orden.fecha && orden.fecha.a) ? new Date(orden.fecha.a, orden.fecha.m - 1, orden.fecha.d) : null
                 };
            })
        );
    },
     paidOrders() {
        return this.allOrders.filter(order => order.total_pagos_realizados > 0);
    },
    presupuestoMetrics() {
        const metrics = {
            ordersWithBudgets: 0,
            total: 0,
            enviados: 0,
            aprobados: 0,
            rechazados: 0,
        };

        this.allOrders.forEach((order) => {
            const presupuestos = Array.isArray(order.presupuestos)
                ? order.presupuestos.filter(
                    (presupuesto) => presupuesto && [1, 2, 10].includes(presupuesto.estado)
                )
                : [];

            if (presupuestos.length > 0) {
                metrics.ordersWithBudgets += 1;
            }

            presupuestos.forEach((presupuesto) => {
                metrics.total += 1;

                if (presupuesto.estado === 1) {
                    metrics.enviados += 1;
                } else if (presupuesto.estado === 2) {
                    metrics.aprobados += 1;
                } else if (presupuesto.estado === 10) {
                    metrics.rechazados += 1;
                }
            });
        });

        return metrics;
    },

     // --- Stat Card Calculations (depend on processed data) ---
      uniqueClients() {
        // Uses paidOrders, which depends on allOrders->solicitudes
        const uniqueClientsMap = new Map();
        this.paidOrders.forEach(order => {
            if (order.cliente && order.cliente.id) {
                const clientId = order.cliente.id;
                 const orderDate = order.fecha_obj || order.fecha_fallback;
                 if (!orderDate) return;

                if (!uniqueClientsMap.has(clientId)) {
                    uniqueClientsMap.set(clientId, { ...order.cliente, firstOrderDate: orderDate });
                } else {
                    const existingClient = uniqueClientsMap.get(clientId);
                    if (orderDate < existingClient.firstOrderDate) {
                        existingClient.firstOrderDate = orderDate;
                    }
                }
            }
        });
        return Array.from(uniqueClientsMap.values());
    },
    sortedUniqueClients() {
        return [...this.uniqueClients].sort((a, b) => a.firstOrderDate - b.firstOrderDate);
    },
    totalClientRequests() {
        return this.solicitudes.filter(sol => !sol.empresa); // Use processed solicitudes
    },
    totalClientRequestsCount() {
        return this.totalClientRequests.length;
    },
    unansweredClientRequestsCount() {
      return this.totalClientRequests.filter(sol => sol.mensajes_profesional_count === 0).length;
    },
    unansweredPercentage() {
      if (this.totalClientRequestsCount === 0) return '0.00';
      const percentage = (this.unansweredClientRequestsCount / this.totalClientRequestsCount) * 100;
      return percentage.toFixed(2);
    },
    totalPayments() {
      return this.pagos.reduce((sum, pago) => sum + pago.monto, 0); // Use processed pagos
    },
    clientIncomePayments() {
      return this.pagos.filter(pago => !pago.empresa && !isJobbingPointsPayment(pago));
    },
    totalPaymentsClients() {
      return this.clientIncomePayments.reduce((sum, pago) => sum + pago.monto, 0);
    },
    totalPaymentsCompany() {
      return this.pagos.filter(pago => pago.empresa).reduce((sum, pago) => sum + pago.monto, 0);
    },

    // --- Chart Data Computations (depend on processed data) ---

    lineChartOptions() { // Income specific options
        let options = JSON.parse(JSON.stringify(this.baseLineChartOptions));
        options.scales.yAxes[0].scaleLabel.labelString = 'Monto ($)';
        options.scales.yAxes[0].ticks.callback = value => '$' + value.toLocaleString('de-DE');
        options.tooltips.callbacks.label = (tooltipItem, data) => `${data.datasets[tooltipItem.datasetIndex].label || ''}: $${tooltipItem.yLabel.toLocaleString('de-DE')}`;
        return options;
    },
    requestLineChartOptions() { // Request specific options
        let options = JSON.parse(JSON.stringify(this.baseLineChartOptions));
        options.scales.yAxes[0].scaleLabel.labelString = 'Cantidad';
        options.scales.yAxes[0].ticks.callback = value => Number.isInteger(value) ? value : '';
        options.tooltips.callbacks.label = (tooltipItem, data) => `${data.datasets[tooltipItem.datasetIndex].label || ''}: ${tooltipItem.yLabel}`;
        return options;
    },
    jobbingCostValues() {
        if (!Array.isArray(this.solicitudes)) return [];

        return this.solicitudes
            .map((solicitud) => normalizeJobbingCost(solicitud.puntos_jobbing_costo))
            .filter((value) => value !== null);
    },
    jobbingCostStats() {
        const values = this.jobbingCostValues;
        const positiveValues = values.filter((value) => value > 0).sort((a, b) => a - b);
        const zeroCostCount = values.filter((value) => value === 0).length;
        const valuesForStats = positiveValues.length > 0
            ? positiveValues
            : [...values].sort((a, b) => a - b);

        if (valuesForStats.length === 0) {
            return {
                total: 0,
                pricedCount: 0,
                zeroCostCount: 0,
                average: 0,
                median: 0,
                min: 0,
                max: 0,
            };
        }

        const totalCost = valuesForStats.reduce((sum, value) => sum + value, 0);

        return {
            total: values.length,
            pricedCount: positiveValues.length,
            zeroCostCount,
            average: Math.round(totalCost / valuesForStats.length),
            median: calculateMedian(valuesForStats),
            min: valuesForStats[0],
            max: valuesForStats[valuesForStats.length - 1],
        };
    },
    jobbingCostDistributionChartData() {
        const values = this.jobbingCostValues;
        if (values.length === 0) {
            return { labels: [], datasets: [] };
        }

        const aggregation = new Map();
        values.forEach((cost) => {
            aggregation.set(cost, (aggregation.get(cost) || 0) + 1);
        });

        const entries = Array.from(aggregation.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([cost, count]) => ({
                cost,
                count,
                label: `${this.formatCurrency(cost)} pts`,
            }));

        return {
            labels: entries.map((entry) => entry.label),
            datasets: [
                {
                    label: 'Solicitudes',
                    data: entries.map((entry) => entry.count),
                    backgroundColor: '#0A9396',
                    borderColor: '#005F73',
                    hoverBackgroundColor: '#94D2BD',
                    hoverBorderColor: '#0A9396',
                    borderWidth: 1,
                    maxBarThickness: 48,
                    costMeta: entries,
                }
            ]
        };
    },
    jobbingCostBarChartOptions() {
        return {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                display: false,
            },
            scales: {
                xAxes: [
                    {
                        type: 'category',
                        scaleLabel: {
                            display: true,
                            labelString: 'Costo en puntos',
                        },
                        ticks: {
                            autoSkip: false,
                            maxRotation: 45,
                            minRotation: 0,
                        },
                        gridLines: {
                            display: false,
                        },
                    }
                ],
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            precision: 0,
                            callback: (value) => this.formatActivityIndex(value),
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Solicitudes',
                        },
                    }
                ],
            },
            tooltips: {
                callbacks: {
                    title: (tooltipItems, data) => {
                        if (!tooltipItems || tooltipItems.length === 0) return '';
                        return data.labels[tooltipItems[0].index] || '';
                    },
                    label: (tooltipItem, data) => {
                        const dataset = data.datasets[tooltipItem.datasetIndex] || {};
                        const meta = Array.isArray(dataset.costMeta)
                            ? dataset.costMeta[tooltipItem.index]
                            : null;
                        const count = meta ? meta.count : tooltipItem.yLabel;
                        const suffix = count === 1 ? 'solicitud' : 'solicitudes';
                        return `${count} ${suffix}`;
                    },
                },
            },
        };
    },
    jobbingPointsTimelineSeries() {
        if (!this.jobbingPointsTimeline || !Array.isArray(this.jobbingPointsTimeline.series)) {
            return [];
        }

        return this.jobbingPointsTimeline.series
            .filter((entry) => entry && entry.date)
            .map((entry) => ({
                date: entry.date,
                displayLabel: formatDayLabel(entry.date),
                purchase_count: Number(entry.purchase_count || 0),
                request_purchase_count: Number(entry.request_purchase_count || 0),
                points_purchased: Number(entry.points_purchased || 0),
                amount_purchased: Number(entry.amount_purchased || 0),
                points_spent: Number(entry.points_spent || 0),
            }))
            .sort((a, b) => a.date.localeCompare(b.date));
    },
    jobbingPointsTimelineChartData() {
        const series = this.jobbingPointsTimelineSeries;
        const hasActivity = series.some((entry) =>
            entry.points_purchased > 0 || entry.points_spent > 0
        );

        if (!hasActivity) {
            return { datasets: [] };
        }

        const pointFor = (entry, value) => ({
            x: entry.date,
            y: value,
            displayLabel: entry.displayLabel,
            purchase_count: entry.purchase_count,
            request_purchase_count: entry.request_purchase_count,
            points_purchased: entry.points_purchased,
            points_spent: entry.points_spent,
            amount_purchased: entry.amount_purchased,
        });

        return {
            datasets: [
                {
                    label: 'Puntos comprados',
                    timelineMetric: 'purchased',
                    data: series.map((entry) => pointFor(entry, entry.points_purchased)),
                    borderColor: '#0A9396',
                    backgroundColor: 'rgba(10, 147, 150, 0.12)',
                    pointBackgroundColor: '#0A9396',
                    fill: false,
                    tension: 0.15,
                },
                {
                    label: 'Puntos usados en solicitudes',
                    timelineMetric: 'spent',
                    data: series.map((entry) => pointFor(entry, entry.points_spent)),
                    borderColor: '#CA6702',
                    backgroundColor: 'rgba(202, 103, 2, 0.12)',
                    pointBackgroundColor: '#CA6702',
                    fill: false,
                    tension: 0.15,
                },
            ],
        };
    },
    jobbingPointsTimelineChartOptions() {
        return {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                display: true,
                position: 'bottom',
            },
            scales: {
                xAxes: [
                    {
                        type: 'time',
                        time: {
                            unit: 'day',
                            displayFormats: {
                                day: 'MMM DD',
                            },
                            tooltipFormat: 'll',
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Día',
                        },
                        ticks: {
                            source: 'auto',
                            maxRotation: 45,
                            autoSkip: true,
                        },
                    },
                ],
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            callback: (value) => `${this.formatCurrency(value)} pts`,
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Puntos',
                        },
                    },
                ],
            },
            tooltips: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: (tooltipItems, data) => {
                        if (!tooltipItems || tooltipItems.length === 0) return '';
                        const tooltipItem = tooltipItems[0];
                        const dataset = data && data.datasets ? data.datasets[tooltipItem.datasetIndex] : null;
                        const point = dataset && Array.isArray(dataset.data) ? dataset.data[tooltipItem.index] : null;
                        return point && point.displayLabel ? point.displayLabel : (tooltipItem.label || tooltipItem.xLabel || '');
                    },
                    label: (tooltipItem, data) => {
                        const dataset = data && data.datasets ? data.datasets[tooltipItem.datasetIndex] : null;
                        const point = dataset && Array.isArray(dataset.data) ? dataset.data[tooltipItem.index] : null;
                        const rawValue = typeof tooltipItem.yLabel === 'number'
                            ? tooltipItem.yLabel
                            : parseFloat(tooltipItem.yLabel);
                        const value = Number.isFinite(rawValue) ? rawValue : 0;
                        const label = dataset && dataset.label ? dataset.label : '';

                        if (dataset && dataset.timelineMetric === 'purchased') {
                            const count = point ? Number(point.purchase_count || 0) : 0;
                            const amount = point ? Number(point.amount_purchased || 0) : 0;
                            return `${label}: ${this.formatCurrency(value)} pts (${count} compras, $${this.formatCurrency(amount)})`;
                        }

                        if (dataset && dataset.timelineMetric === 'spent') {
                            const count = point ? Number(point.request_purchase_count || 0) : 0;
                            return `${label}: ${this.formatCurrency(value)} pts (${count} solicitudes)`;
                        }

                        return `${label}: ${this.formatCurrency(value)} pts`;
                    },
                },
            },
            hover: {
                mode: 'nearest',
                intersect: true,
            },
        };
    },
    professionalActivity() {
        return {
            ...createEmptyProfessionalActivityIndex(),
            ...(this.professionalActivityMetrics || {}),
        };
    },
    professionalActivityHistorySeries() {
        if (!this.professionalActivityHistory || !Array.isArray(this.professionalActivityHistory.series)) {
            return [];
        }

        return this.professionalActivityHistory.series
            .filter((entry) => entry && entry.period_start)
            .map((entry) => ({
                ...entry,
                index: Number(entry.index || 0),
                normalized_index: Number(entry.normalized_index || 0),
                active_professionals_count: Number(entry.active_professionals_count || 0),
                eligible_professionals_count: Number(entry.eligible_professionals_count || 0),
                snapshots_count: Number(entry.snapshots_count || 0),
            }));
    },
    professionalActivityHistoryChartData() {
        const series = this.professionalActivityHistorySeries;
        if (series.length === 0) {
            return { datasets: [] };
        }

        return {
            datasets: [
                {
                    label: 'Índice actividad (pts)',
                    data: series.map((entry) => ({
                        x: entry.period_start,
                        y: entry.index,
                    })),
                    borderColor: '#264653',
                    backgroundColor: 'rgba(38, 70, 83, 0.12)',
                    pointBackgroundColor: '#264653',
                    fill: true,
                    tension: 0.15,
                    yAxisID: 'activity-index',
                    activityMeta: series,
                },
                {
                    label: 'Profesionales activos',
                    data: series.map((entry) => ({
                        x: entry.period_start,
                        y: entry.active_professionals_count,
                    })),
                    borderColor: '#2A9D8F',
                    backgroundColor: 'rgba(42, 157, 143, 0.08)',
                    pointBackgroundColor: '#2A9D8F',
                    fill: false,
                    tension: 0.15,
                    borderDash: [5, 4],
                    yAxisID: 'active-professionals',
                    activityMeta: series,
                },
            ],
        };
    },
    professionalActivityHistoryChartOptions() {
        const granularity = this.activityHistoryGranularity;
        const timeUnit = granularity === 'hour'
            ? 'hour'
            : (granularity === 'week' ? 'week' : 'day');
        const xLabel = granularity === 'hour'
            ? 'Hora'
            : (granularity === 'week' ? 'Semana' : 'Día');

        return {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                display: true,
                position: 'bottom',
            },
            scales: {
                xAxes: [
                    {
                        type: 'time',
                        time: {
                            unit: timeUnit,
                            displayFormats: {
                                hour: 'MMM D HH:mm',
                                day: 'MMM D',
                                week: 'MMM D',
                            },
                            tooltipFormat: granularity === 'hour' ? 'MMM D, HH:mm' : 'll',
                        },
                        scaleLabel: {
                            display: true,
                            labelString: xLabel,
                        },
                        ticks: {
                            source: 'auto',
                            maxRotation: 45,
                            autoSkip: true,
                        },
                    },
                ],
                yAxes: [
                    {
                        id: 'activity-index',
                        position: 'left',
                        ticks: {
                            beginAtZero: true,
                            callback: (value) => `${this.formatActivityIndex(value)} pts`,
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Puntos',
                        },
                    },
                    {
                        id: 'active-professionals',
                        position: 'right',
                        gridLines: {
                            drawOnChartArea: false,
                        },
                        ticks: {
                            beginAtZero: true,
                            precision: 0,
                            callback: (value) => this.formatActivityIndex(value),
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Activos',
                        },
                    },
                ],
            },
            tooltips: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: (tooltipItem, data) => {
                        const dataset = data && data.datasets ? data.datasets[tooltipItem.datasetIndex] : null;
                        const label = dataset && dataset.label ? dataset.label : '';
                        const meta = dataset && Array.isArray(dataset.activityMeta)
                            ? dataset.activityMeta[tooltipItem.index]
                            : null;
                        const value = typeof tooltipItem.yLabel === 'number'
                            ? tooltipItem.yLabel
                            : parseFloat(tooltipItem.yLabel);

                        if (dataset && dataset.yAxisID === 'activity-index') {
                            const snapshotsCount = meta ? Number(meta.snapshots_count || 0) : 0;
                            const suffix = snapshotsCount > 1 ? `, prom. ${snapshotsCount} snapshots` : '';
                            return `${label}: ${this.formatActivityIndex(value)} pts${suffix}`;
                        }

                        return `${label}: ${this.formatActivityIndex(value)}`;
                    },
                },
            },
            hover: {
                mode: 'nearest',
                intersect: true,
            },
        };
    },
    jobbingPointsRepurchaseSummary() {
        return {
            ...createEmptyJobbingPointsRepurchase().summary,
            ...(this.jobbingPointsRepurchase && this.jobbingPointsRepurchase.summary
                ? this.jobbingPointsRepurchase.summary
                : {}),
        };
    },
    jobbingPointsRepurchaseProfessionals() {
        if (!this.jobbingPointsRepurchase || !Array.isArray(this.jobbingPointsRepurchase.professionals)) {
            return [];
        }
        return this.jobbingPointsRepurchase.professionals;
    },
    topJobbingPointsRepurchaseProfessionals() {
        return [...this.jobbingPointsRepurchaseProfessionals]
            .sort((a, b) => {
                const repurchaseDelta = Number(b.repurchase_count || 0) - Number(a.repurchase_count || 0);
                if (repurchaseDelta !== 0) return repurchaseDelta;
                const repurchaseAmountDelta = Number(b.repurchase_amount || 0) - Number(a.repurchase_amount || 0);
                if (repurchaseAmountDelta !== 0) return repurchaseAmountDelta;
                return Number(b.amount_total || 0) - Number(a.amount_total || 0);
            })
            .slice(0, 25);
    },
    jobbingPointsRepurchaseMonthlySeries() {
        if (!this.jobbingPointsRepurchase || !Array.isArray(this.jobbingPointsRepurchase.monthly_series)) {
            return [];
        }
        return this.jobbingPointsRepurchase.monthly_series;
    },
    jobbingPointsRepurchaseChartData() {
        const series = this.jobbingPointsRepurchaseMonthlySeries;
        if (series.length === 0) {
            return { labels: [], datasets: [] };
        }

        return {
            labels: series.map((entry) => this.formatMonthKey(entry.month)),
            datasets: [
                {
                    label: 'Primeras compras',
                    data: series.map((entry) => Number(entry.first_purchase_count || 0)),
                    backgroundColor: '#5FA8D3',
                    borderColor: '#1B4965',
                    borderWidth: 1,
                    maxBarThickness: 44,
                },
                {
                    label: 'Recompras',
                    data: series.map((entry) => Number(entry.repurchase_count || 0)),
                    backgroundColor: '#2A9D8F',
                    borderColor: '#1F7A6F',
                    borderWidth: 1,
                    maxBarThickness: 44,
                },
            ],
        };
    },
    jobbingPointsRepurchaseAmountChartData() {
        const summary = this.jobbingPointsRepurchaseSummary;
        const firstPurchaseAmount = Math.max(
            0,
            Number(summary.amount_total || 0) - Number(summary.repurchase_amount_total || 0),
        );
        const repurchaseAmount = Number(summary.repurchase_amount_total || 0);

        if (firstPurchaseAmount <= 0 && repurchaseAmount <= 0) {
            return { labels: [], datasets: [] };
        }

        return {
            labels: ['Primeras compras', 'Recompras'],
            datasets: [
                {
                    label: 'Ingresos por puntos',
                    data: [firstPurchaseAmount, repurchaseAmount],
                    backgroundColor: ['#5FA8D3', '#2A9D8F'],
                    hoverBorderWidth: 4,
                    hoverOffset: 8,
                },
            ],
        };
    },
    jobbingPointsRepurchaseBarChartOptions() {
        return {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                display: true,
                position: 'bottom',
            },
            scales: {
                xAxes: [
                    {
                        stacked: false,
                        gridLines: {
                            display: false,
                        },
                    },
                ],
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            precision: 0,
                            callback: (value) => Number.isInteger(value) ? value : '',
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Compras',
                        },
                    },
                ],
            },
            tooltips: {
                mode: 'index',
                intersect: false,
            },
        };
    },
    jobbingPointsRetentionSummary() {
        return {
            ...createEmptyJobbingPointsRetention().summary,
            ...(this.jobbingPointsRetention && this.jobbingPointsRetention.summary
                ? this.jobbingPointsRetention.summary
                : {}),
        };
    },
    jobbingPointsRetentionCriteria() {
        return {
            ...createEmptyJobbingPointsRetention().criteria,
            ...(this.jobbingPointsRetention && this.jobbingPointsRetention.criteria
                ? this.jobbingPointsRetention.criteria
                : {}),
        };
    },
    jobbingPointsRetentionProfessionals() {
        if (!this.jobbingPointsRetention || !Array.isArray(this.jobbingPointsRetention.professionals)) {
            return [];
        }
        return this.jobbingPointsRetention.professionals;
    },
    jobbingPointsLapsedRetentionProfessionals() {
        return this.jobbingPointsRetention.lapsed_professionals || [];
    },
    filteredLapsedRetentionProfessionals() {
        const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const search = normalize(this.lapsedRetentionSearch).trim();
        if (!search) return this.jobbingPointsLapsedRetentionProfessionals;
        return this.jobbingPointsLapsedRetentionProfessionals.filter((professional) =>
            [professional.nombre, professional.empresa_nombre, professional.email, professional.telefono, professional.profesional_id]
                .some((value) => normalize(value).includes(search))
        );
    },
    jobbingPointsRetentionHistoryChartData() {
        const series = (
            this.jobbingPointsRetentionHistory
            && Array.isArray(this.jobbingPointsRetentionHistory.series)
        )
            ? this.jobbingPointsRetentionHistory.series
            : [];
        if (series.length === 0) {
            return { labels: [], datasets: [] };
        }

        return {
            labels: series.map((entry) => this.formatDateString(`${entry.date}T12:00:00`)),
            datasets: [
                {
                    label: 'Activados',
                    data: series.map((entry) => Number(entry.activated_professionals_count || 0)),
                    borderColor: '#78909C',
                    backgroundColor: 'rgba(120, 144, 156, 0.12)',
                    pointBackgroundColor: '#78909C',
                    fill: false,
                    lineTension: 0.18,
                    yAxisID: 'retention-counts',
                },
                {
                    label: 'Cohorte evaluable',
                    data: series.map((entry) => Number(entry.eligible_professionals_count || 0)),
                    borderColor: '#3D5A80',
                    backgroundColor: 'rgba(61, 90, 128, 0.12)',
                    pointBackgroundColor: '#3D5A80',
                    fill: false,
                    lineTension: 0.18,
                    yAxisID: 'retention-counts',
                },
                {
                    label: 'Recurrentes activos',
                    data: series.map((entry) => Number(entry.recurring_professionals_count || 0)),
                    borderColor: '#2A9D8F',
                    backgroundColor: 'rgba(42, 157, 143, 0.14)',
                    pointBackgroundColor: '#2A9D8F',
                    borderWidth: 3,
                    fill: false,
                    lineTension: 0.18,
                    yAxisID: 'retention-counts',
                },
                {
                    label: 'Recurrentes dormidos',
                    data: series.map((entry) => Number(entry.lapsed_recurring_professionals_count || 0)),
                    borderColor: '#9C6644',
                    backgroundColor: 'rgba(156, 102, 68, 0.12)',
                    pointBackgroundColor: '#9C6644',
                    borderDash: [5, 4],
                    fill: false,
                    lineTension: 0.18,
                    yAxisID: 'retention-counts',
                },
                {
                    label: 'Tasa de recurrencia',
                    data: series.map((entry) => Number(entry.early_retention_rate || 0)),
                    borderColor: '#E76F51',
                    backgroundColor: 'rgba(231, 111, 81, 0.1)',
                    pointBackgroundColor: '#E76F51',
                    borderWidth: 2,
                    fill: false,
                    lineTension: 0.18,
                    yAxisID: 'retention-rate',
                },
            ],
        };
    },
    jobbingPointsRetentionHistoryChartOptions() {
        return {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                display: true,
                position: 'bottom',
            },
            scales: {
                xAxes: [
                    {
                        type: 'category',
                        gridLines: {
                            display: false,
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Cierre semanal',
                        },
                    },
                ],
                yAxes: [
                    {
                        id: 'retention-counts',
                        position: 'left',
                        ticks: {
                            beginAtZero: true,
                            precision: 0,
                            callback: (value) => Number.isInteger(value) ? value : '',
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Profesionales',
                        },
                    },
                    {
                        id: 'retention-rate',
                        position: 'right',
                        gridLines: {
                            drawOnChartArea: false,
                        },
                        ticks: {
                            beginAtZero: true,
                            min: 0,
                            max: 100,
                            callback: (value) => `${value}%`,
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Tasa de recurrencia',
                        },
                    },
                ],
            },
            tooltips: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: (tooltipItem, data) => {
                        const dataset = data.datasets[tooltipItem.datasetIndex];
                        const value = Number(tooltipItem.yLabel || 0);
                        if (dataset.yAxisID === 'retention-rate') {
                            return `${dataset.label}: ${this.formatPercentage(value)}`;
                        }
                        return `${dataset.label}: ${this.formatCurrency(value)}`;
                    },
                },
            },
            hover: {
                mode: 'nearest',
                intersect: true,
            },
        };
    },
    conversionRateChartOptions() {
        const viewMode = this.conversionViewMode;

        if (viewMode === 'monthly') {
            const monthlyChartData = this.conversionRateChartData;
            const monthlyDataset = monthlyChartData?.datasets?.[0] || null;
            const monthlyRates = Array.isArray(monthlyDataset?.data)
                ? monthlyDataset.data.filter(value => Number.isFinite(value))
                : [];

            const ticks = createPercentageTicks(monthlyRates);

            const options = {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    xAxes: [
                        {
                            type: 'category',
                            scaleLabel: {
                                display: true,
                                labelString: 'Mes',
                            },
                            gridLines: {
                                display: false,
                            },
                        }
                    ],
                    yAxes: [
                        {
                            ticks,
                            scaleLabel: {
                                display: true,
                                labelString: 'Porcentaje (%)',
                            },
                            gridLines: {
                                color: 'rgba(0, 0, 0, 0.05)',
                            },
                        }
                    ],
                },
                tooltips: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                        title: (tooltipItems, data) => {
                            if (!tooltipItems || tooltipItems.length === 0) return '';
                            const tooltipItem = tooltipItems[0];
                            const dataset = data && data.datasets ? data.datasets[tooltipItem.datasetIndex] : null;
                            const metaList = dataset && Array.isArray(dataset.conversionMeta) ? dataset.conversionMeta : [];
                            const meta = metaList[tooltipItem.index] || null;
                            if (meta && meta.displayLabel) {
                                return meta.displayLabel;
                            }
                            if (Array.isArray(data?.labels) && data.labels[tooltipItem.index]) {
                                return data.labels[tooltipItem.index];
                            }
                            return tooltipItem.label || '';
                        },
                        label: (tooltipItem, data) => {
                            const dataset = data && data.datasets ? data.datasets[tooltipItem.datasetIndex] : null;
                            const metaList = dataset && Array.isArray(dataset.conversionMeta) ? dataset.conversionMeta : [];
                            const meta = metaList[tooltipItem.index] || null;
                            const rawRate = typeof tooltipItem.yLabel === 'number' ? tooltipItem.yLabel : parseFloat(tooltipItem.yLabel);
                            const rate = Number.isFinite(rawRate)
                                ? rawRate
                                : (meta && typeof meta.rate === 'number' ? meta.rate : 0);
                            const converted = meta && Number.isFinite(meta.converted) ? meta.converted : 0;
                            const total = meta && Number.isFinite(meta.total) ? meta.total : 0;
                            const label = dataset && dataset.label ? dataset.label : '';
                            const formattedRate = Number.isFinite(rate) ? rate.toFixed(2) : '0.00';
                            if (total > 0) {
                                return `${label}: ${formattedRate}% (${converted}/${total})`;
                            }
                            return `${label}: ${formattedRate}%`;
                        },
                    },
                },
                legend: {
                    display: true,
                    position: 'bottom',
                },
                hover: {
                    mode: 'nearest',
                    intersect: true,
                },
            };

            return options;
        }

        const options = JSON.parse(JSON.stringify(this.baseLineChartOptions));
        const xAxis = options?.scales?.xAxes?.[0] || {};
        xAxis.time = xAxis.time || {};
        xAxis.time.unit = 'day';
        xAxis.time.displayFormats = {
            ...xAxis.time.displayFormats,
            day: 'MMM DD',
        };
        xAxis.time.tooltipFormat = 'll';
        xAxis.scaleLabel = xAxis.scaleLabel || {};
        xAxis.scaleLabel.labelString = 'Día';

        options.scales = options.scales || { xAxes: [xAxis], yAxes: [{}] };
        options.scales.xAxes = options.scales.xAxes || [xAxis];
        options.scales.xAxes[0] = xAxis;

        options.scales.yAxes = options.scales.yAxes || [{}];
        const yAxis = { ...(options.scales.yAxes[0] || {}) };
        yAxis.scaleLabel = yAxis.scaleLabel || {};
        yAxis.scaleLabel.labelString = 'Porcentaje (%)';

        const dailyDatasets = Array.isArray(this.conversionRateChartData?.datasets)
            ? this.conversionRateChartData.datasets
            : [];

        const dailyRates = dailyDatasets
            .flatMap((dataset) => {
                if (!dataset || !Array.isArray(dataset.data)) {
                    return [];
                }
                return dataset.data.map((point) => {
                    if (Number.isFinite(point)) {
                        return point;
                    }
                    if (point && typeof point === 'object' && Number.isFinite(point.y)) {
                        return point.y;
                    }
                    return null;
                });
            })
            .filter((value) => Number.isFinite(value) && value >= 0);

        yAxis.ticks = createPercentageTicks(dailyRates);
        options.scales.yAxes[0] = yAxis;

        options.tooltips = options.tooltips || {};
        const tooltipCallbacks = options.tooltips.callbacks || {};
        tooltipCallbacks.title = (tooltipItems, data) => {
            if (!tooltipItems || tooltipItems.length === 0) return '';
            const tooltipItem = tooltipItems[0];
            const dataset = data && data.datasets ? data.datasets[tooltipItem.datasetIndex] : null;
            const point = dataset && Array.isArray(dataset.data) ? dataset.data[tooltipItem.index] : null;
            if (point && point.displayLabel) {
                return point.displayLabel;
            }
            return tooltipItem.label || tooltipItem.xLabel || '';
        };
        tooltipCallbacks.label = (tooltipItem, data) => {
            const dataset = data && data.datasets ? data.datasets[tooltipItem.datasetIndex] : null;
            const point = dataset && Array.isArray(dataset.data) ? dataset.data[tooltipItem.index] : null;
            const rawRate = typeof tooltipItem.yLabel === 'number' ? tooltipItem.yLabel : parseFloat(tooltipItem.yLabel);
            const rate = Number.isFinite(rawRate)
                ? rawRate
                : (point && typeof point.y === 'number' ? point.y : 0);
            const numerator = point && Number.isFinite(point.numerator)
                ? point.numerator
                : (point && Number.isFinite(point.converted)
                    ? point.converted
                    : (point && Number.isFinite(point.responded) ? point.responded : 0));
            const total = point && Number.isFinite(point.total) ? point.total : 0;
            const label = dataset && dataset.label ? dataset.label : '';
            const formattedRate = Number.isFinite(rate) ? rate.toFixed(2) : '0.00';
            if (total > 0) {
                return `${label}: ${formattedRate}% (${numerator}/${total})`;
            }
            return `${label}: ${formattedRate}%`;
        };
        options.tooltips.callbacks = tooltipCallbacks;

        return options;
    },

 paymentsByUtmSourceChartData() {
        console.log('[Computed: paymentsByUtmSourceChartData] Generating...');
        const aggregation = new Map();
        const defaultValue = "Sin identificar";
        let includedPaymentsCount = 0;
        let totalAggregatedAmount = 0;

        if (!Array.isArray(this.pagos) || this.pagos.length === 0) {
             console.log('[Computed: paymentsByUtmSourceChartData] No processed payments found.');
             return formatPieChartData(aggregation, 'Pagos por UTM Source'); // Return empty structure
        }

        console.log(`[Computed: paymentsByUtmSourceChartData] Processing ${this.pagos.length} confirmed payments...`);

        this.pagos.forEach((pago, index) => { // Iterate through confirmed payments from computed prop
            let utmSource = defaultValue;
            let logPrefix = `[PaymentsUTMSource] Pago Index ${index} (ID: ${pago.id}, Monto: ${pago.monto}):`;

            // Determine UTM Source Key
            if (pago.solicitud_cliente && pago.solicitud_cliente.utm_source) {
                utmSource = pago.solicitud_cliente.utm_source;
            } 

            // Aggregate
            const currentTotal = aggregation.get(utmSource) || 0;
            const newTotal = currentTotal + pago.monto;
            aggregation.set(utmSource, newTotal);
            includedPaymentsCount++;
            totalAggregatedAmount += pago.monto;

            // Log the specific aggregation step
            if (index < 20 || utmSource !== defaultValue) { // Log first 20 and any identified ones
                console.log(`${logPrefix} UTM Key='${utmSource}'. Aggregating: ${currentTotal} + ${pago.monto} = ${newTotal}`);
            }
        });

        console.log(`[Computed: paymentsByUtmSourceChartData] Finished processing. Included ${includedPaymentsCount} payments. Total aggregated amount: ${totalAggregatedAmount}`);
        console.log('[Computed: paymentsByUtmSourceChartData] Final Aggregation Map:', aggregation);

        return formatPieChartData(aggregation, 'Pagos por UTM Source');
    },
    paymentsByUtmCampaignChartData() {
        console.log('[Computed: paymentsByUtmSourceChartData] Generating...');
        const aggregation = new Map();
        const defaultValue = "Sin identificar";
        let includedPaymentsCount = 0;
        let totalAggregatedAmount = 0;

        if (!Array.isArray(this.pagos) || this.pagos.length === 0) {
             console.log('[Computed: paymentsByUtmSourceChartData] No processed payments found.');
             return formatPieChartData(aggregation, 'Pagos por UTM Source'); // Return empty structure
        }

        console.log(`[Computed: paymentsByUtmSourceChartData] Processing ${this.pagos.length} confirmed payments...`);

        this.pagos.forEach((pago, index) => { // Iterate through confirmed payments from computed prop
            let utmSource = defaultValue;
            let logPrefix = `[PaymentsUTMSource] Pago Index ${index} (ID: ${pago.id}, Monto: ${pago.monto}):`;

            // Determine UTM Source Key
            if (pago.solicitud_cliente && pago.solicitud_cliente.utm_campaign) {
                utmSource = pago.solicitud_cliente.utm_campaign;
            } 

            // Aggregate
            const currentTotal = aggregation.get(utmSource) || 0;
            const newTotal = currentTotal + pago.monto;
            aggregation.set(utmSource, newTotal);
            includedPaymentsCount++;
            totalAggregatedAmount += pago.monto;

            // Log the specific aggregation step
            if (index < 20 || utmSource !== defaultValue) { // Log first 20 and any identified ones
                console.log(`${logPrefix} UTM Key='${utmSource}'. Aggregating: ${currentTotal} + ${pago.monto} = ${newTotal}`);
            }
        });

        console.log(`[Computed: paymentsByUtmSourceChartData] Finished processing. Included ${includedPaymentsCount} payments. Total aggregated amount: ${totalAggregatedAmount}`);
        console.log('[Computed: paymentsByUtmSourceChartData] Final Aggregation Map:', aggregation);

        return formatPieChartData(aggregation, 'Pagos por UTM Source');
    },
    requestsByUtmSourceChartData() {
        const aggregationMap = aggregateChartData(this.solicitudes, 'utm_source');
        return formatPieChartData(aggregationMap, 'Solicitudes por UTM Source');
    },
     requestsByUtmCampaignChartData() {
        const aggregationMap = aggregateChartData(this.solicitudes, 'utm_campaign');
        return formatPieChartData(aggregationMap, 'Solicitudes por UTM Campaign');
    },
     requestsByClassChartData() {
        const aggregationMap = aggregateChartData(this.solicitudes, 'clase');
        return formatPieChartData(aggregationMap, 'Solicitudes por Clase');
    },
    salesByServiceChartData() {
        const serviceSales = new Map();
        this.paidOrders.forEach(order => {
            (order.servicios || []).forEach(servicio => {
                const name = servicio.nombre || "Servicio sin nombre";
                const price = (typeof servicio.precio === 'number') ? servicio.precio : 0;
                serviceSales.set(name, (serviceSales.get(name) || 0) + price);
            });
        });
        return formatPieChartData(serviceSales, 'Ventas por Servicio');
    },
    salesByClassChartData() {
        const aggregationMap = aggregateChartData(
            this.paidOrders,
            'solicitud_cliente.clase',
            'total_pagos_realizados'
        );
        return formatPieChartData(aggregationMap, 'Ventas por Clase de Solicitud');
    },

    incomeLineChartData() {
        console.log('[Computed: incomeLineChartData] Generating...');
        if (!this.dt_dia || !this.dt_dia2) return { datasets: [] };
        const dateRange = getDateRangeArray(this.dt_dia, this.dt_dia2);
        if (dateRange.length === 0) return { datasets: [] };

        const paymentsByDate = groupBy(this.pagos, 'fecha_iso_str'); // Group processed payments

        const dataCompany = dateRange.map(dateStr => ({
            x: dateStr,
            y: (paymentsByDate[dateStr] || []).filter(p => p.empresa).reduce((sum, p) => sum + p.monto, 0)
        }));
        const dataClients = dateRange.map(dateStr => ({
            x: dateStr,
            y: (paymentsByDate[dateStr] || [])
                .filter(p => !p.empresa && !isJobbingPointsPayment(p))
                .reduce((sum, p) => sum + p.monto, 0)
        }));
         const dataTotal = dateRange.map(dateStr => ({
            x: dateStr,
            y: (paymentsByDate[dateStr] || []).reduce((sum, p) => sum + p.monto, 0)
        }));

        const chartData = {
            datasets: [
                { label: "Ingreso Empresas", data: dataCompany, borderColor: '#3D9970', backgroundColor: 'rgba(61, 153, 112, 0.1)', fill: true, tension: 0.1 },
                { label: "Ingreso Clientes", data: dataClients, borderColor: '#36A2EB', backgroundColor: 'rgba(54, 162, 235, 0.1)', fill: true, tension: 0.1 },
                { label: "Ingreso Total", data: dataTotal, borderColor: '#ff6384', backgroundColor: 'rgba(255, 99, 132, 0.1)', fill: true, tension: 0.1 }
            ]
        };
        console.log('[Computed: incomeLineChartData] Result:', JSON.parse(JSON.stringify(chartData))); // Log deep copy
        return chartData;
    },

    requestsLineChartData() {
         console.log('[Computed: requestsLineChartData] Generating...');
         if (!this.dt_dia || !this.dt_dia2) return { datasets: [] };
         const dateRange = getDateRangeArray(this.dt_dia, this.dt_dia2);
         if (dateRange.length === 0) return { datasets: [] };

         const requestsByDate = groupBy(this.solicitudes, 'fecha_iso_str'); // Group processed solicitudes

         const dataCompany = dateRange.map(dateStr => ({
             x: dateStr,
             y: (requestsByDate[dateStr] || []).filter(s => s.empresa).length
         }));
         const dataClients = dateRange.map(dateStr => ({
             x: dateStr,
             y: (requestsByDate[dateStr] || []).filter(s => !s.empresa).length
         }));
         const dataTotal = dateRange.map(dateStr => ({
             x: dateStr,
             y: (requestsByDate[dateStr] || []).length
         }));

          const chartData = {
            datasets: [
                 { label: "Solicitudes Empresa", data: dataCompany, borderColor: '#3D9970', backgroundColor: 'rgba(61, 153, 112, 0.1)', fill: true, tension: 0.1 },
                 { label: "Solicitudes Clientes", data: dataClients, borderColor: '#36A2EB', backgroundColor: 'rgba(54, 162, 235, 0.1)', fill: true, tension: 0.1 },
                 { label: "Solicitudes Total", data: dataTotal, borderColor: '#ff6384', backgroundColor: 'rgba(255, 99, 132, 0.1)', fill: true, tension: 0.1 }
            ]
        };
        console.log('[Computed: requestsLineChartData] Result:', JSON.parse(JSON.stringify(chartData))); // Log deep copy
        return chartData;
    },

    conversionRateChartData() {
        if (!this.dt_dia2) return { datasets: [] };

        const endDateStr = this.dt_dia2;

        if (!Array.isArray(this.rawSolicitudes) || this.rawSolicitudes.length === 0) {
            return { datasets: [] };
        }

        if (this.conversionViewMode === 'monthly') {
            const startBoundary = this.dt_dia || null;
            const months = getMonthSequenceBetween(startBoundary, endDateStr);
            if (!months.length) {
                return { datasets: [] };
            }

            const monthLookup = new Map(months.map((month) => [month.monthKey, month]));
            const totalsByMonth = new Map();
            const convertedByMonth = new Map();

            this.solicitudes.forEach((solicitud) => {
                if (!solicitud || !solicitud.fecha_iso_str) return;

                const dateStr = solicitud.fecha_iso_str;
                const monthKey = dateStr.slice(0, 7);

                if (!monthLookup.has(monthKey)) return;

                totalsByMonth.set(monthKey, (totalsByMonth.get(monthKey) || 0) + 1);

                if (solicitudHasPaidOrder(solicitud)) {
                    convertedByMonth.set(monthKey, (convertedByMonth.get(monthKey) || 0) + 1);
                }
            });

            const datasetMeta = months.map((month) => {
                const total = totalsByMonth.get(month.monthKey) || 0;
                const converted = convertedByMonth.get(month.monthKey) || 0;
                const rate = total > 0 ? parseFloat(((converted / total) * 100).toFixed(2)) : 0;
                const axisLabel = month.axisLabel || month.label || month.monthKey;
                const displayLabel = month.label || formatMonthLabel(month.key) || axisLabel;
                return {
                    rate,
                    total,
                    converted,
                    displayLabel,
                    axisLabel,
                    monthKey: month.monthKey,
                };
            });

            const hasMonthlyData = datasetMeta.some(point => point.total > 0);
            if (!hasMonthlyData) {
                return { datasets: [] };
            }

            const labels = datasetMeta.map(point => point.axisLabel);
            const rates = datasetMeta.map(point => point.rate);

            return {
                labels,
                datasets: [
                    {
                        label: 'Tasa de conversión (%)',
                        data: rates,
                        backgroundColor: '#8e44ad',
                        borderColor: '#6c3483',
                        hoverBackgroundColor: '#a569bd',
                        hoverBorderColor: '#512e5f',
                        borderWidth: 1,
                        maxBarThickness: 36,
                        conversionMeta: datasetMeta,
                    }
                ]
            };
        }

        if (this.conversionViewMode === 'dailyRate') {
            const lookbackDays = 29;
            const fallbackStart = subtractDays(endDateStr, lookbackDays) || endDateStr;
            const selectedStart = this.dt_dia || fallbackStart;
            const dateRange = getDateRangeArray(selectedStart, endDateStr);
            if (dateRange.length === 0) {
                return { datasets: [] };
            }

            const totalsByDate = new Map();
            const convertedByDate = new Map();

            this.rawSolicitudes.forEach((solicitud) => {
                if (!solicitud || !solicitud.fecha_creado) return;
                const dateStr = solicitud.fecha_creado.split('T')[0];
                if (!dateStr || dateStr < selectedStart || dateStr > endDateStr) return;

                totalsByDate.set(dateStr, (totalsByDate.get(dateStr) || 0) + 1);

                if (solicitudHasPaidOrder(solicitud)) {
                    convertedByDate.set(dateStr, (convertedByDate.get(dateStr) || 0) + 1);
                }
            });

            const dataset = dateRange.map((dateStr) => {
                const total = totalsByDate.get(dateStr) || 0;
                const converted = convertedByDate.get(dateStr) || 0;
                const rate = total > 0 ? parseFloat(((converted / total) * 100).toFixed(2)) : 0;
                return {
                    x: dateStr,
                    y: rate,
                    total,
                    converted,
                    numerator: converted,
                    displayLabel: formatDayLabel(dateStr),
                };
            });

            const hasDailyRateData = dataset.some(point => point.total > 0);
            if (!hasDailyRateData) {
                return { datasets: [] };
            }

            return {
                datasets: [
                    {
                        label: 'Tasa de conversión diaria (%)',
                        data: dataset,
                        borderColor: '#2E86C1',
                        backgroundColor: 'rgba(46, 134, 193, 0.15)',
                        fill: true,
                        tension: 0.15,
                    }
                ]
            };
        }

        const lookbackDays = 29;
        const fallbackStart = subtractDays(endDateStr, lookbackDays) || endDateStr;
        const selectedStart = this.dt_dia || fallbackStart;
        const paddedStartCandidate = subtractDays(selectedStart, lookbackDays);
        const dataStart = paddedStartCandidate && paddedStartCandidate < selectedStart
            ? paddedStartCandidate
            : selectedStart;

        const aggregatedRange = getDateRangeArray(dataStart, endDateStr);
        if (aggregatedRange.length === 0) {
            return { datasets: [] };
        }

        const totalsByDate = new Map();
        const convertedByDate = new Map();
        const clientResponsesByDate = new Map();

        this.rawSolicitudes.forEach((solicitud) => {
            if (!solicitud || !solicitud.fecha_creado) return;
            const dateStr = solicitud.fecha_creado.split('T')[0];
            if (!dateStr || dateStr < dataStart || dateStr > endDateStr) return;

            totalsByDate.set(dateStr, (totalsByDate.get(dateStr) || 0) + 1);

            if (solicitudHasPaidOrder(solicitud)) {
                convertedByDate.set(dateStr, (convertedByDate.get(dateStr) || 0) + 1);
            }

            if (solicitudHasClientResponse(solicitud)) {
                clientResponsesByDate.set(dateStr, (clientResponsesByDate.get(dateStr) || 0) + 1);
            }
        });

        const prefixTotals = [];
        const indexByDate = new Map();
        let cumulativeTotal = 0;
        let cumulativeConverted = 0;
        let cumulativeResponded = 0;

        aggregatedRange.forEach((dateStr, index) => {
            const dailyTotal = totalsByDate.get(dateStr) || 0;
            const dailyConverted = convertedByDate.get(dateStr) || 0;
            const dailyResponded = clientResponsesByDate.get(dateStr) || 0;
            cumulativeTotal += dailyTotal;
            cumulativeConverted += dailyConverted;
            cumulativeResponded += dailyResponded;
            prefixTotals.push({
                dateStr,
                cumulativeTotal,
                cumulativeConverted,
                cumulativeResponded,
            });
            indexByDate.set(dateStr, index);
        });

        const dateRange = getDateRangeArray(selectedStart, endDateStr);
        if (dateRange.length === 0) {
            return { datasets: [] };
        }

        const conversionSeries = dateRange.map((dateStr) => {
            const endIndex = indexByDate.get(dateStr);
            if (typeof endIndex === 'undefined') {
                return {
                    x: dateStr,
                    y: 0,
                    total: 0,
                    converted: 0,
                    numerator: 0,
                    displayLabel: formatDayLabel(dateStr),
                };
            }

            const windowStartCandidate = subtractDays(dateStr, lookbackDays) || dateStr;
            const windowStart = windowStartCandidate && windowStartCandidate > dataStart ? windowStartCandidate : dataStart;
            const startIndex = indexByDate.get(windowStart);

            const endTotals = prefixTotals[endIndex];
            const beforeTotals = typeof startIndex === 'number' && startIndex > 0
                ? prefixTotals[startIndex - 1]
                : null;

            const total = endTotals.cumulativeTotal - (beforeTotals ? beforeTotals.cumulativeTotal : 0);
            const converted = endTotals.cumulativeConverted - (beforeTotals ? beforeTotals.cumulativeConverted : 0);
            const rate = total > 0 ? parseFloat(((converted / total) * 100).toFixed(2)) : 0;

            return {
                x: dateStr,
                y: rate,
                total,
                converted,
                numerator: converted,
                displayLabel: formatDayLabel(dateStr),
            };
        });

        const responseSeries = dateRange.map((dateStr) => {
            const endIndex = indexByDate.get(dateStr);
            if (typeof endIndex === 'undefined') {
                return {
                    x: dateStr,
                    y: 0,
                    total: 0,
                    responded: 0,
                    numerator: 0,
                    displayLabel: formatDayLabel(dateStr),
                };
            }

            const windowStartCandidate = subtractDays(dateStr, lookbackDays) || dateStr;
            const windowStart = windowStartCandidate && windowStartCandidate > dataStart ? windowStartCandidate : dataStart;
            const startIndex = indexByDate.get(windowStart);

            const endTotals = prefixTotals[endIndex];
            const beforeTotals = typeof startIndex === 'number' && startIndex > 0
                ? prefixTotals[startIndex - 1]
                : null;

            const total = endTotals.cumulativeTotal - (beforeTotals ? beforeTotals.cumulativeTotal : 0);
            const responded = endTotals.cumulativeResponded - (beforeTotals ? beforeTotals.cumulativeResponded : 0);
            const rate = total > 0 ? parseFloat(((responded / total) * 100).toFixed(2)) : 0;

            return {
                x: dateStr,
                y: rate,
                total,
                responded,
                numerator: responded,
                displayLabel: formatDayLabel(dateStr),
            };
        });

        const hasConversionData = conversionSeries.some(point => point.total > 0);
        const hasResponseData = responseSeries.some(point => point.total > 0);

        if (this.conversionViewMode === 'dailyClientResponse') {
            if (!hasResponseData) {
                return { datasets: [] };
            }

            return {
                datasets: [
                    {
                        label: 'Solicitudes con respuesta de cliente (%)',
                        data: responseSeries,
                        borderColor: '#27ae60',
                        backgroundColor: 'rgba(39, 174, 96, 0.1)',
                        fill: false,
                        tension: 0.15,
                    }
                ]
            };
        }

        if (!hasConversionData && !hasResponseData) {
            return { datasets: [] };
        }

        const datasets = [
            {
                label: 'Tasa de conversión (%)',
                data: conversionSeries,
                borderColor: '#8e44ad',
                backgroundColor: 'rgba(142, 68, 173, 0.1)',
                fill: true,
                tension: 0.15,
            },
        ];

        if (hasResponseData) {
            datasets.push({
                label: 'Solicitudes con respuesta de cliente (%)',
                data: responseSeries,
                borderColor: '#27ae60',
                backgroundColor: 'rgba(39, 174, 96, 0.1)',
                fill: false,
                tension: 0.15,
            });
        }

        return { datasets };
    },
  },

  methods: {
    ...mapActions(['initialize']),

    openLapsedRetentionProfessionals() {
      this.lapsedRetentionSearch = '';
      this.showLapsedRetentionProfessionals = true;
    },

    selectReportTab(tabName) {
      const previousTab = this.activeReportTab;
      if (tabName === 'subscriptions' && !this.subscriptionRangeInitialized) {
        this.setAcquisitionMonthToDateRange();
        this.subscriptionRangeInitialized = true;
      }
      if (tabName === 'acquisition-profitability' && !this.acquisitionRangeInitialized) {
        this.setAcquisitionMonthToDateRange();
        this.acquisitionRangeInitialized = true;
      }
      this.activeReportTab = tabName;
      if (tabName === 'jobbing-points-retention') {
        this.fetchJobbingPointsRetentionHistory();
      }
      if (
        ['acquisition-profitability', 'subscriptions', 'professional-interactions'].includes(previousTab)
        && !this.isIndependentReport
      ) {
        this.fetchData();
      }
    },

    localDateString(value) {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    setAcquisitionMonthToDateRange() {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      this.dt_dia = this.localDateString(firstDay);
      this.dt_dia2 = this.localDateString(today);
      this.acquisitionRangeInitialized = true;
    },

    onDateRangeChange() {
      if (!this.isIndependentReport) {
        this.fetchData();
      }
    },

    async fetchJobbingPointsRetentionHistory(force = false) {
      if (!this.dt_dia2) {
        return;
      }

      const requestedEndDate = this.dt_dia2;
      if (
        !force
        && this.retentionHistoryLoadedFor === requestedEndDate
        && !this.retentionHistoryError
      ) {
        return;
      }

      this.isRetentionHistoryLoading = true;
      this.retentionHistoryError = null;
      try {
        const hostname = window.hostname || '';
        const response = await axios.get(
          `${hostname}api/admin/jobbing_points_retention_history`,
          {
            params: {
              end_date: requestedEndDate,
              window_weeks: 52,
            },
          }
        );

        if (this.dt_dia2 !== requestedEndDate) {
          return;
        }
        this.jobbingPointsRetentionHistory = this.normalizeJobbingPointsRetentionHistory(
          response && response.data
        );
        this.retentionHistoryLoadedFor = requestedEndDate;
        this.chartKey += 1;
      } catch (error) {
        if (this.dt_dia2 !== requestedEndDate) {
          return;
        }
        console.error('[fetchJobbingPointsRetentionHistory] Error:', error);
        this.jobbingPointsRetentionHistory = createEmptyJobbingPointsRetentionHistory();
        this.retentionHistoryLoadedFor = null;
        this.retentionHistoryError = (
          error.response?.data?.error
          || error.response?.data?.message
          || 'No se pudo cargar la evolución semanal.'
        );
      } finally {
        if (this.dt_dia2 === requestedEndDate) {
          this.isRetentionHistoryLoading = false;
        }
      }
    },

    onConversionViewModeChange(newMode) {
        if (newMode && newMode !== this.conversionViewMode) {
            this.conversionViewMode = newMode;
        }
    },

    // Helper to check if chart data is valid for rendering
    hasData(chartData) {
        return chartData && chartData.datasets && chartData.datasets.some(ds => ds.data && ds.data.length > 0);
    },

    formatCurrency(value) {
      return Intl.NumberFormat('de-DE').format(value || 0);
    },
    formatActivityIndex(value) {
      const numericValue = typeof value === 'number' ? value : parseFloat(value);
      if (!Number.isFinite(numericValue)) return '0';

      return Intl.NumberFormat('de-DE', {
        minimumFractionDigits: numericValue < 100 && numericValue % 1 !== 0 ? 1 : 0,
        maximumFractionDigits: numericValue < 100 ? 1 : 0,
      }).format(numericValue);
    },
    formatActivityAge(hours) {
      const numericValue = typeof hours === 'number' ? hours : parseFloat(hours);
      if (!Number.isFinite(numericValue)) return '-';
      if (numericValue < 1) {
        return `${Math.round(numericValue * 60)} min`;
      }

      return `${Intl.NumberFormat('de-DE', {
        maximumFractionDigits: 1,
      }).format(numericValue)} h`;
    },
    formatPercentage(value, decimals = 1) {
      const numericValue = typeof value === 'number' ? value : parseFloat(value);
      if (!Number.isFinite(numericValue)) return '0%';
      return `${numericValue.toFixed(decimals)}%`;
    },
    formatDateString(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      });
    },
    formatMonthKey(monthKey) {
      if (!monthKey || typeof monthKey !== 'string') return 'Sin fecha';
      if (!/^\d{4}-\d{2}$/.test(monthKey)) return monthKey;
      const date = new Date(`${monthKey}-01T00:00:00Z`);
      if (Number.isNaN(date.getTime())) return monthKey;
      return date.toLocaleDateString('es-ES', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
    },
    formatDate(dateObject) {
        if (!dateObject || !(dateObject instanceof Date) || isNaN(dateObject.getTime())) return 'Fecha inválida';
        try { return dateObject.toLocaleDateString("es-ES", { year: 'numeric', month: 'long', day: 'numeric' }); }
        catch (e) { console.error("Error formatting date:", dateObject, e); return 'Error fecha'; }
    },

    normalizeHeatmapData(payload) {
      const empty = createEmptyHeatmapData();
      if (!payload || typeof payload !== 'object') {
        return empty;
      }

      const weeks = Array.isArray(payload.weeks)
        ? payload.weeks
            .filter((week) => week && week.start)
            .map((week) => ({
              start: week.start,
              end: week.end || week.start,
              label: week.label || '',
              partial: Boolean(week.partial),
            }))
        : [];

      const professionals = Array.isArray(payload.professionals)
        ? payload.professionals
            .filter((prof) => prof && typeof prof.id !== 'undefined')
            .map((prof) => ({
              id: prof.id,
              name: prof.name || prof.full_name || 'Profesional sin nombre',
            }))
        : [];

      const values = Array.isArray(payload.values)
        ? payload.values
            .filter((entry) => entry && entry.professional_id !== undefined && entry.week_start)
            .map((entry) => {
              const total = Number.isFinite(entry.total)
                ? entry.total
                : parseInt(entry.total, 10);
              const approved = Number.isFinite(entry.approved)
                ? entry.approved
                : parseInt(entry.approved, 10);
              return {
                professional_id: entry.professional_id,
                week_start: entry.week_start,
                total: Number.isFinite(total) ? total : 0,
                approved: Number.isFinite(approved) ? approved : 0,
              };
            })
        : [];

      return {
        weeks,
        professionals,
        values,
      };
    },

    normalizeJobbingPointsRepurchase(payload) {
      const empty = createEmptyJobbingPointsRepurchase();
      if (!payload || typeof payload !== 'object') {
        return empty;
      }

      return {
        summary: {
          ...empty.summary,
          ...(payload.summary && typeof payload.summary === 'object' ? payload.summary : {}),
        },
        professionals: Array.isArray(payload.professionals) ? payload.professionals : [],
        monthly_series: Array.isArray(payload.monthly_series) ? payload.monthly_series : [],
      };
    },

    normalizeJobbingPointsRetention(payload) {
      const empty = createEmptyJobbingPointsRetention();
      if (!payload || typeof payload !== 'object') {
        return empty;
      }

      return {
        ...empty,
        ...payload,
        criteria: {
          ...empty.criteria,
          ...(payload.criteria && typeof payload.criteria === 'object' ? payload.criteria : {}),
        },
        summary: {
          ...empty.summary,
          ...(payload.summary && typeof payload.summary === 'object' ? payload.summary : {}),
        },
        professionals: Array.isArray(payload.professionals) ? payload.professionals : [],
        lapsed_professionals: Array.isArray(payload.lapsed_professionals) ? payload.lapsed_professionals : [],
      };
    },

    normalizeJobbingPointsRetentionHistory(payload) {
      const empty = createEmptyJobbingPointsRetentionHistory();
      if (!payload || typeof payload !== 'object') {
        return empty;
      }

      return {
        ...empty,
        ...payload,
        criteria: payload.criteria && typeof payload.criteria === 'object'
          ? payload.criteria
          : {},
        series: Array.isArray(payload.series) ? payload.series : [],
      };
    },

    normalizeJobbingPointsTimeline(payload) {
      const empty = createEmptyJobbingPointsTimeline();
      if (!payload || typeof payload !== 'object') {
        return empty;
      }

      return {
        ...empty,
        ...payload,
        summary: {
          ...empty.summary,
          ...(payload.summary && typeof payload.summary === 'object' ? payload.summary : {}),
        },
        series: Array.isArray(payload.series) ? payload.series : [],
      };
    },

    normalizeProfessionalActivityHistory(payload) {
      const empty = createEmptyProfessionalActivityHistory();
      if (!payload || typeof payload !== 'object') {
        return empty;
      }

      return {
        ...empty,
        ...payload,
        series: Array.isArray(payload.series) ? payload.series : [],
      };
    },

    setInitialDateRange() {
      const today = new Date();
      const endDate = new Date();
      this.dt_dia2 = this.localDateString(endDate);

      const startDate = new Date();
      startDate.setDate(today.getDate() - 7);
      this.dt_dia = this.localDateString(startDate);
    },

    validateDateRange() {
        if (!this.dt_dia || !this.dt_dia2) {
            this.error = "Por favor seleccione un rango de fechas."; return false;
        }
         if (this.dt_dia > this.dt_dia2) {
            this.error = "La fecha 'Desde' no puede ser posterior a la fecha 'Hasta'."; return false;
        }
        this.error = null; return true;
    },

    async fetchData() {
      this.showLapsedRetentionProfessionals = false;
      console.log("fetchData triggered");
      if (!this.validateDateRange()) {
          this.isLoading = false;
          this.rawSolicitudes = []; // Clear data on validation error
          this.rawPagos = [];
          this.heatmapData = createEmptyHeatmapData();
          this.jobbingPointsMetrics = createEmptyJobbingPointsMetrics();
          this.jobbingPointsTimeline = createEmptyJobbingPointsTimeline();
          this.professionalActivityMetrics = createEmptyProfessionalActivityIndex();
          this.professionalActivityHistory = createEmptyProfessionalActivityHistory();
          this.jobbingPointsRepurchase = createEmptyJobbingPointsRepurchase();
          this.jobbingPointsRetention = createEmptyJobbingPointsRetention();
          this.jobbingPointsRetentionHistory = createEmptyJobbingPointsRetentionHistory();
          this.isRetentionHistoryLoading = false;
          this.retentionHistoryError = null;
          this.retentionHistoryLoadedFor = null;
          this.chartKey += 1; // Force clear charts
          return;
      }

      this.isLoading = true;
      this.error = null;
      this.rawSolicitudes = [];
      this.rawPagos = [];
      this.heatmapData = createEmptyHeatmapData();
      this.jobbingPointsMetrics = createEmptyJobbingPointsMetrics();
      this.jobbingPointsTimeline = createEmptyJobbingPointsTimeline();
      this.professionalActivityMetrics = createEmptyProfessionalActivityIndex();
      this.professionalActivityHistory = createEmptyProfessionalActivityHistory();
      this.jobbingPointsRepurchase = createEmptyJobbingPointsRepurchase();
      this.jobbingPointsRetention = createEmptyJobbingPointsRetention();
      if (this.retentionHistoryLoadedFor !== this.dt_dia2) {
        this.jobbingPointsRetentionHistory = createEmptyJobbingPointsRetentionHistory();
        this.isRetentionHistoryLoading = false;
        this.retentionHistoryError = null;
        this.retentionHistoryLoadedFor = null;
      }
      const extendedStartDate = subtractDays(this.dt_dia, 30) || this.dt_dia;
      const params = {
        start_date: extendedStartDate,
        end_date: this.dt_dia2,
        metrics_start_date: this.dt_dia,
        activity_granularity: this.activityHistoryGranularity,
      };

      try {
        // Ensure hostname is defined, provide a default if necessary
        const hostname = window.hostname || '';
        if (hostname === '') {
            console.warn("window.hostname is not set. Assuming relative API path.");
        }
        const apiUrl = `${hostname}api/admin/get_reporte`;
        const heatmapUrl = `${hostname}api/admin/presupuesto_approval_heatmap`;
        console.log(`[fetchData] Fetching from: ${apiUrl} with params:`, params);
        console.log(`[fetchData] Selected range: ${this.dt_dia} to ${this.dt_dia2}`);
        console.log(`[fetchData] Fetching heatmap from: ${heatmapUrl} with params:`, params);

        const [reportResponse, heatmapResponse] = await Promise.all([
          axios.get(apiUrl, { params }),
          axios.get(heatmapUrl, { params }),
        ]);

        console.log("[fetchData] Raw API Response:", reportResponse?.data);

        // Basic validation of response structure
        if (typeof reportResponse?.data !== 'object' || reportResponse.data === null) {
             throw new Error("Respuesta de API inválida o vacía.");
        }

        this.rawSolicitudes = Array.isArray(reportResponse.data.solicitudes) ? reportResponse.data.solicitudes : [];
        this.rawPagos = Array.isArray(reportResponse.data.pagos) ? reportResponse.data.pagos : [];
        this.jobbingPointsMetrics = {
          ...createEmptyJobbingPointsMetrics(),
          ...(reportResponse.data.jobbing_points_metrics || {}),
        };
        this.jobbingPointsTimeline = this.normalizeJobbingPointsTimeline(
          reportResponse.data.jobbing_points_timeline
        );
        this.professionalActivityMetrics = {
          ...createEmptyProfessionalActivityIndex(),
          ...(reportResponse.data.professional_activity_index || {}),
        };
        this.professionalActivityHistory = this.normalizeProfessionalActivityHistory(
          reportResponse.data.professional_activity_history
        );
        this.jobbingPointsRepurchase = this.normalizeJobbingPointsRepurchase(
          reportResponse.data.jobbing_points_repurchase
        );
        this.jobbingPointsRetention = this.normalizeJobbingPointsRetention(
          reportResponse.data.jobbing_points_retention
        );
        if (this.activeReportTab === 'jobbing-points-retention') {
          await this.fetchJobbingPointsRetentionHistory();
        }

        console.log(`[fetchData] Received ${this.rawSolicitudes.length} solicitudes and ${this.rawPagos.length} pagos.`);

        console.log('[fetchData] Heatmap response:', heatmapResponse?.data);
        this.heatmapData = this.normalizeHeatmapData(heatmapResponse?.data);

        // Check if data looks reasonable (optional)
        if (this.rawSolicitudes.length > 0 && typeof this.rawSolicitudes[0].fecha_creado === 'undefined') {
            console.warn("First solicitud is missing 'fecha_creado'. Check API response structure.");
        }
        if (this.rawPagos.length > 0 && typeof this.rawPagos[0].fecha_creado === 'undefined') {
            console.warn("First pago is missing 'fecha_creado'. Check API response structure.");
        }


        this.chartKey += 1; // Increment key AFTER data is potentially updated
      } catch (error) {
        console.error('[fetchData] Error fetching report data:', error);
        this.error = `Error al obtener datos: ${error.response?.data?.message || error.message || 'Error desconocido'}`;
        this.rawSolicitudes = []; // Clear data on error
        this.rawPagos = [];
        this.heatmapData = createEmptyHeatmapData();
        this.jobbingPointsMetrics = createEmptyJobbingPointsMetrics();
        this.jobbingPointsTimeline = createEmptyJobbingPointsTimeline();
        this.professionalActivityMetrics = createEmptyProfessionalActivityIndex();
        this.professionalActivityHistory = createEmptyProfessionalActivityHistory();
        this.jobbingPointsRepurchase = createEmptyJobbingPointsRepurchase();
        this.jobbingPointsRetention = createEmptyJobbingPointsRetention();
        this.jobbingPointsRetentionHistory = createEmptyJobbingPointsRetentionHistory();
        this.isRetentionHistoryLoading = false;
        this.retentionHistoryError = null;
        this.retentionHistoryLoadedFor = null;
        this.chartKey += 1; // Force clear charts
      } finally {
        this.isLoading = false;
        // Adding a slight delay before potentially forcing another update,
        // sometimes helps if reactivity takes a moment.
        this.$nextTick(() => {
            this.chartKey += 1;
        });
      }
    },
  },

  mounted() {
    console.log("Dashboard component mounted.");
    // Define hostname globally if not already done (e.g., in main.js or .env)
    if (typeof window.hostname === 'undefined') {
         console.warn("`window.hostname` is not defined. Setting to empty string for relative paths.");
         window.hostname = ''; // Default to relative path if not set elsewhere
    }
    this.setInitialDateRange();
    this.fetchData(); // Fetch initial data
  },
}
</script>

<style scoped>
/* Styles remain the same as the previous correct version */
/* Base Layout */
.dashboard-layout { display: flex; flex-direction: column; min-height: 100vh; background-color: #f2f7fb; text-align: left; }
.column-left { /* Assuming SideNav handles its own positioning */ }
.column-right { flex-grow: 1; width: 100%; min-height: 100vh; padding-top: 80px; padding-left: 260px; box-sizing: border-box; transition: padding-left 0.3s ease; }
.main-container { margin: 20px; padding: 25px 30px; border-radius: 8px; background-color: #ffffff; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); }
/* Header */
.dashboard-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; flex-wrap: wrap; gap: 15px; }
.dashboard-header h2 { margin: 0; color: #333; font-weight: 600; }
.date-range-picker { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.date-range-picker label { font-size: 0.9em; color: #555; }
.date-input { padding: 6px 10px; border: 1px solid #ccc; border-radius: 4px; font-size: 0.9em; }
.date-preset-button { padding: 7px 11px; border: 1px solid #b9cfd7; border-radius: 6px; background-color: #f5fafb; color: #356778; font-size: 0.85em; font-weight: 600; cursor: pointer; }
.date-preset-button:hover { background-color: #eaf4f6; border-color: #8fb5c1; }
/* Loading & Error States */
.loading-indicator, .error-message { text-align: center; padding: 40px 20px; font-size: 1.1em; color: #555; border-radius: 6px; margin: 20px 0; }
.error-message { background-color: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; font-weight: 500; }
.error-message strong { font-weight: bold; }
/* Stats Grid */
.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; margin-bottom: 40px; }
.report-tabs { display: flex; align-items: stretch; flex-wrap: wrap; width: 100%; gap: 4px; box-sizing: border-box; background-color: #eef3f7; border: 1px solid #dbe5ed; border-radius: 8px; padding: 4px; margin-bottom: 24px; }
.report-tab { display: flex; flex: 1 1 170px; align-items: center; justify-content: center; min-height: 42px; border: 0; background: transparent; color: #52616c; border-radius: 6px; padding: 9px 14px; font-size: 14px; font-weight: 600; line-height: 1.25; text-align: center; cursor: pointer; transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease; }
.report-tab.active { background-color: #ffffff; color: #1f3444; box-shadow: 0 1px 2px rgba(31, 52, 68, 0.12); }
.report-tab-panel { min-width: 0; }
.metrics-section { margin-bottom: 40px; }
.metrics-section-title { margin: 0 0 16px; color: #444; font-size: 18px; font-weight: 600; border-bottom: 1px solid #eee; padding-bottom: 10px; }
.budget-stats-grid, .jobbing-points-stats-grid { margin-bottom: 0; }
.activity-history-block { margin-bottom: 40px; }
.jobbing-points-timeline-block { margin-top: 24px; }
.chart-header-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 15px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
.chart-header-row h4 { margin: 0; border-bottom: 0; padding-bottom: 0; }
.chart-header-detail { display: block; margin-top: 4px; color: #74818c; font-size: 12px; }
.chart-controls { display: flex; align-items: center; gap: 8px; }
.view-select { padding: 6px 10px; border-radius: 4px; border: 1px solid #ccc; font-size: 0.9em; color: #333; background-color: #fff; }
.view-select:focus { outline: none; box-shadow: 0 0 0 2px rgba(54, 162, 235, 0.4); border-color: #36a2eb; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
.stat-card { color: #fff; border-radius: 8px; padding: 20px; display: flex; justify-content: space-between; align-items: center; position: relative; overflow: hidden; min-height: 100px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
.stat-card-content { z-index: 1; }
.stat-card-button { border: 0; width: 100%; text-align: left; font: inherit; cursor: pointer; transition: box-shadow 0.15s ease; }
.stat-card-button .stat-card-content > span { display: block; }
.stat-card-button:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
.stat-card-button:focus-visible { outline: 3px solid #243746; outline-offset: 3px; }
.stat-card-action { margin-top: 10px; font-size: 12px; font-weight: 700; text-decoration: underline; }
.stat-title { font-size: 13px; margin-bottom: 5px; opacity: 0.9; }
.stat-value { font-size: 24px; font-weight: 600; }
.stat-value span { font-size: 0.6em; opacity: 0.8; font-weight: 400; margin-left: 5px; }
.stat-card-detail { color: inherit; font-size: 11px; line-height: 1.35; opacity: 0.84; max-width: 190px; }
.stat-card-icon { font-size: 45px; opacity: 0.2; position: absolute; right: 15px; top: 50%; transform: translateY(-50%); z-index: 0; }
.repurchase-heading { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 20px; border-bottom: 1px solid #e8eef3; padding-bottom: 16px; }
.repurchase-heading h3 { margin: 0 0 6px; color: #263946; font-size: 22px; font-weight: 700; }
.repurchase-heading p { margin: 0; color: #65727d; font-size: 14px; }
.range-pill { display: inline-flex; align-items: center; white-space: nowrap; background-color: #f6f8fa; border: 1px solid #dde6ee; border-radius: 999px; color: #50616f; font-size: 13px; font-weight: 600; padding: 7px 12px; }
.retention-definition { margin-bottom: 24px; padding: 16px 18px; border: 1px solid #cfe2e4; border-left: 4px solid #2a9d8f; border-radius: 8px; background-color: #f4fbfa; color: #334e52; }
.retention-definition > strong { display: block; margin-bottom: 5px; color: #1f5558; font-size: 15px; }
.retention-definition p { margin: 0 0 12px; color: #557074; font-size: 13px; line-height: 1.5; }
.retention-criteria-list { display: flex; flex-wrap: wrap; gap: 8px; }
.retention-criterion { display: inline-flex; align-items: center; padding: 5px 9px; border: 1px solid #bcd8d8; border-radius: 999px; background-color: #ffffff; color: #315f62; font-size: 12px; font-weight: 700; }
.repurchase-stats-grid { margin-bottom: 24px; }
.repurchase-insights-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 14px; margin-bottom: 32px; }
.insight-card { background-color: #f8fafc; border: 1px solid #e2eaf0; border-radius: 8px; padding: 14px 16px; min-height: 82px; display: flex; flex-direction: column; justify-content: center; gap: 6px; }
.insight-label { color: #65727d; font-size: 12px; font-weight: 700; text-transform: uppercase; }
.insight-card strong { color: #253847; font-size: 22px; line-height: 1.2; }
.repurchase-charts-grid { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.65fr); gap: 24px; margin-bottom: 34px; }
.repurchase-chart-container, .repurchase-pie-container { min-height: 380px; display: flex; flex-direction: column; }
.repurchase-table-section { margin-top: 12px; }
.repurchase-table-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
.repurchase-table-header h4 { margin: 0; color: #444; font-weight: 600; border-bottom: 0; padding-bottom: 0; }
.repurchase-table-header span { color: #74818c; font-size: 13px; font-weight: 600; }
.table-responsive { overflow-x: auto; border: 1px solid #e4ebf1; border-radius: 8px; }
.repurchase-table { width: 100%; min-width: 980px; border-collapse: collapse; background-color: #fff; }
.retention-table { min-width: 1180px; }
.lapsed-retention-description { color: #5d6b76; font-size: 14px; }
.lapsed-retention-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 12px; }
.lapsed-retention-toolbar input { flex: 1 1 280px; }
.lapsed-retention-toolbar > span { color: #65727d; font-size: 13px; }
.lapsed-retention-table-container { min-height: 0; overflow: auto; }
.repurchase-table.lapsed-retention-table th { position: sticky; top: 0; z-index: 1; white-space: normal; min-width: 110px; }
.lapsed-retention-table th:nth-child(2) { text-align: left; }
.lapsed-retention-table th:first-child, .lapsed-retention-table td:first-child { position: sticky; left: 0; z-index: 2; background-color: #fff; }
.lapsed-retention-table th:first-child { z-index: 3; background-color: #f8fafc; }
.lapsed-retention-table .lapsed-retention-contact { text-align: left; }
.retention-history-block { margin-bottom: 34px; }
.retention-history-chart-container { min-height: 430px; display: flex; flex-direction: column; }
.history-error-message { flex-grow: 1; display: flex; align-items: center; justify-content: center; min-height: 180px; margin: 0; padding: 24px; border: 1px solid #f2c7c7; border-radius: 6px; background-color: #fff6f6; color: #9b3b3b; text-align: center; }
.repurchase-table th, .repurchase-table td { padding: 11px 12px; border-bottom: 1px solid #edf2f6; color: #394b59; font-size: 13px; vertical-align: middle; text-align: right; white-space: nowrap; }
.repurchase-table th:first-child, .repurchase-table td:first-child { text-align: left; min-width: 220px; }
.repurchase-table th { background-color: #f8fafc; color: #5d6b76; font-weight: 700; text-transform: uppercase; font-size: 11px; letter-spacing: 0; }
.repurchase-table tbody tr:hover { background-color: #fbfdff; }
.repurchase-table tbody tr:last-child td { border-bottom: 0; }
.repurchase-table strong { display: block; color: #243746; font-size: 14px; line-height: 1.25; }
.table-meta { display: block; margin-top: 3px; color: #758591; font-size: 12px; white-space: normal; }
/* Charts Section */
.charts-section { display: flex; flex-direction: column; gap: 40px; }
.chart-block h4 { margin-bottom: 20px; color: #444; font-weight: 600; border-bottom: 1px solid #eee; padding-bottom: 10px; }
.pie-charts-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 30px; margin-top: 25px; }
.chart-container { background-color: #f8f9fa; padding: 20px; border-radius: 6px; box-shadow: inset 0 1px 2px rgba(0,0,0,0.05); }
.chart-container.pie-chart-container { min-height: 350px; display: flex; flex-direction: column; }
.price-summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-bottom: 16px; }
.price-summary-item { background-color: #f8f9fa; border: 1px solid #e6edf2; border-radius: 6px; padding: 12px 14px; display: flex; flex-direction: column; gap: 4px; min-height: 74px; justify-content: center; }
.price-summary-label { color: #65727d; font-size: 12px; font-weight: 600; text-transform: uppercase; }
.price-summary-item strong { color: #243746; font-size: 18px; font-weight: 600; line-height: 1.25; }
.price-distribution-chart-container { min-height: 380px; display: flex; flex-direction: column; }
.activity-history-chart-container { min-height: 380px; display: flex; flex-direction: column; }
.jobbing-points-timeline-container { min-height: 380px; display: flex; flex-direction: column; }
.chart-container >>> .chartjs-render-monitor { flex-grow: 1; } /* Allow canvas to grow */
.chart-title { text-align: center; font-size: 1em; font-weight: 500; color: #666; margin-bottom: 15px; }
.no-data-message { text-align: center; color: #888; padding: 40px 10px; font-style: italic; flex-grow: 1; display: flex; align-items: center; justify-content: center; min-height: 100px; background-color: #f8f9fa; border-radius: 4px;} /* Added bg for visibility */
/* Client List */
.client-list-section { margin-top: 20px; }
.client-list-section h4 { margin-bottom: 15px; color: #444; font-weight: 600; }
.client-list { list-style: none; padding: 0; max-height: 300px; overflow-y: auto; border: 1px solid #eee; border-radius: 4px; }
.client-list li { padding: 8px 12px; border-bottom: 1px solid #eee; font-size: 0.9em; color: #555; }
.client-list li:last-child { border-bottom: none; }
/* Responsive Adjustments */
@media (max-width: 992px) { .main-container { padding: 20px; } }
@media (max-width: 800px) { .column-right { padding-top: 70px; padding-right: 15px; padding-left: 15px; } .main-container { margin: 10px 0; } }
@media (max-width: 768px) { .dashboard-header { flex-direction: column; align-items: flex-start; } .date-range-picker { width: 100%; flex-wrap: wrap; } .stats-grid { grid-template-columns: 1fr; } .pie-charts-row { grid-template-columns: 1fr; } .repurchase-heading { flex-direction: column; } .repurchase-charts-grid { grid-template-columns: 1fr; } .report-tab { flex-basis: 140px; } }

</style>

<style>
/* BootstrapVue renders the modal in body, outside this view's scoped styles.
   Percentage dimensions respect the app's body zoom without changing it. */
#lapsed-retention-modal___BV_modal_outer_ {
  z-index: 10040 !important;
}
#lapsed-retention-modal___BV_modal_outer_ > .modal-backdrop {
  width: 100%;
  height: 100%;
}
#lapsed-retention-modal .modal-dialog {
  width: calc(100% - 48px);
  max-width: 1800px;
  height: calc(100% - 48px);
  min-height: 0;
  max-height: calc(100% - 48px);
  margin: 24px auto;
}
#lapsed-retention-modal .modal-content {
  max-height: 100%;
}
#lapsed-retention-modal .modal-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
#lapsed-retention-modal .lapsed-retention-description,
#lapsed-retention-modal .lapsed-retention-toolbar,
#lapsed-retention-modal .modal-body > .table-meta {
  flex-shrink: 0;
}
#lapsed-retention-modal .lapsed-retention-table-container {
  flex: 0 1 auto;
}
@media (max-width: 575.98px) {
  #lapsed-retention-modal .modal-dialog {
    width: calc(100% - 24px);
    height: calc(100% - 24px);
    max-height: calc(100% - 24px);
    margin: 12px auto;
  }
}
</style>
